/**
 * Boot order is the entire point of v2 (learned 2026-07-15 the hard way):
 *   1. paint whatever we have, instantly (IndexedDB cache)
 *   2. repair auth in the background — visibly
 *   3. stream fresh data in behind a pill
 * Phase 2 adds the other direction: every EDIT paints instantly, queues in
 * the offline outbox, and syncs when the network allows — "3 changes will
 * sync", never a spinner, never a lost edit.
 */
import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Auth, type AuthState } from "./lib/auth";
import { backendFetch } from "./lib/backend";
import { isRestrictedRole, hiddenPagesFor, hiddenFieldsFor, readOnlyPagesFor, canSeePageFor, firstVisiblePage, canDoAction, leadScopeFor, PAGE_LABELS, NAV_PAGES as NAV_PAGE_IDS, type Page } from "./lib/access";
import { loadView, saveView } from "./lib/view";
import { startBuildWatch, currentIndexAsset } from "./lib/buildWatch";
import { diag, diagStart, diagFlush } from "./lib/diag";
import { fmtStaleAge, freshness, subscribeHealth } from "./lib/health";
import { idbGet, idbSet } from "./lib/idb";
import { Outbox, type OutboxSnapshot } from "./lib/outbox";
import { natKey, taskKey } from "./lib/natkey";
import {
  cachedEdits, cachedTasks, fetchEdits, fetchTasks, sendOps, isTaskDone,
  buildStageChecklist, incompleteChecklist,
  reconcileDeals, WF_TEMPLATE_VERSION,
  opSetStatus, opSetNotes, opSetArv, opSetField, opPutTask, opPatchTask, opDelTask,
  opSaveSeller, opClearSeller, opSaveMediaLinks,
  type EditsMap, type TaskRec, type SellerContact, type MediaLink,
} from "./features/edits/api";
import { applyOpsToDeals, applyOpsToEdits, applyOpsToTasks } from "./features/edits/replay";
import {
  cachedLeads, dismissProperty, fetchLeads, fetchManualLeads, opTombsAddLeads,
  fetchNewLeads, mergeNewLeads, newestLeadMs,
  buildManualLeadRow, opAddLead, opDelLead, isManualLead, narrowManualLead,
  manualKey, fullAddress, type AddPropertyForm,
} from "./features/leads/api";
import { cachedThreads, fetchThreads, type Thread } from "./features/inbox/api";
import { cachedDeals, fetchDeals, probeDealsWrite, opMoveDealStage, opEditDealField, opEditDealBlob, opAssignDeal, opDeleteDeal, opDealDriveLabel, stageName as stageNameOf, shiftDealDate, type Deal } from "./features/deals/api";
import { cachedWholesale, fetchWholesale, narrowWtFlyer, opEditWholesale, opEditWholesaleField, opPutDispo, opPutWtList, opSaveWtFlyer, type WholesaleDeal, type DispoKind, type WtListKey, type WtFlyer } from "./features/wholesale/api";
import {
  cachedContacts, contactOpsForDelete, contactOpsForMerge, contactOpsForSave, fetchContacts,
  narrowContact, type Contact,
} from "./features/contacts/api";
import { cachedSharedViews, fetchSharedViews, opDelSharedView, opPutSharedView, type SharedView, type ViewSpec } from "./features/views/api";
import {
  fetchCompPhotos, opApplyCompsArv, opPersistCompsTool, searchComps,
  type CompRow, type CompsFilters, type SavedComp,
} from "./features/comps/api";
import { opSaveArvResult, runArvEstimate, type ArvResult } from "./features/arv/api";
import { opSaveRehabCalc, type RehabCalcState } from "./features/rehab/api";
import { fetchLeadLog, fetchLeadThreads, fetchMailAccounts, fetchMailBody, fetchMailInbox, fetchMailMessageFull, sendMail } from "./features/mail/api";
import type { MailLoaders } from "./features/mail/EmailsSection";
import { MailboxPage, type MailboxLoaders } from "./features/mail/MailboxPage";
import { createTransactionFolders, fetchDriveBlob, fetchDriveCategories, fetchDriveFiles, fetchPdfPage, fetchPdfPages, uploadDriveFile, DRIVE_MSG } from "./features/drive/api";
import type { DriveLoaders } from "./features/drive/DriveSection";
import { liveOn } from "./lib/liveFire";
import { runAiPermitSearch } from "./features/permits/api";
import { ContactsPage } from "./features/contacts/ContactsPage";
import { collectPastContacts } from "./features/contacts/pastContacts";
import { cachedCalendar, fetchCalendar, opReplaceCalendarGroup, type CalEvent, type CalGroups } from "./features/calendar/api";
import { CalendarPage } from "./features/calendar/CalendarPage";
import { DashboardPage } from "./features/dashboard/DashboardPage";
import { HotPage } from "./features/hot/HotPage";
import { RoiPage } from "./features/roi/RoiPage";
import { mergeMaster } from "./features/roi/math";
import { Palette } from "./features/palette/Palette";
import { BottomNav } from "./features/nav/BottomNav";
import { Icon, type IconName } from "./features/shell/icons";
import { AskAiModal, type AskAiDeal } from "./features/shell/AskAiModal";
import { DeadOpsModal, type DeadOpView } from "./features/common/DeadOpsModal";
import { DevicePreview } from "./features/shell/DevicePreview";
import { cachedOrgSettings, fetchOrgSettings, fetchSettingsEtags, isStaleWrite, rebaseLedgerOps, mergeLedgerOps, opPutFeedUrl, opPutSetting, type WritableSettingKey } from "./features/settings/api";
import { applyIntent, diffLedger, isEmptyIntent } from "./features/settings/ledgerIntent";
import { SettingsPage } from "./features/settings/SettingsPage";
import { opPutPartners, opPutScopeTpls, type Partner } from "./features/settings/api";
import { loadUiPrefs, saveUiPrefs as saveUiPrefsRemote, type UiPrefs } from "./features/settings/uiPrefs";
import { opPutContractors } from "./features/contractors/api";
import { ExpensesPage } from "./features/expenses/ExpensesPage";
import { CompanyExpensesPage } from "./features/expenses/CompanyExpensesPage";
import { UtilitiesPage } from "./features/expenses/UtilitiesPage";
import { ScrapersPage } from "./features/scrapers/ScrapersPage";
import {
  fetchScraperHealth, fetchFetchStats, fetchScraperJobs,
  saveScraperJob, toggleScraperJob, deleteScraperJob, pauseAllScrapers,
  type ScraperSource,
} from "./features/scrapers/api";
import { opPutPropExpenses, opPutCompanyExpenses, opPutTxnExpenses, allTxnExpensesRaw, txnExpenseToRaw, newExpenseCid } from "./features/expenses/api";
import { InvestorsPage } from "./features/investors/InvestorsPage";
import { opPutInvestors, type Investor } from "./features/investors/api";
import { DealInboxPage } from "./features/dealinbox/DealInboxPage";
import { fetchInboxStatus, parseText as inboxParseText, parseImage as inboxParseImage, scanGmail } from "./features/dealinbox/api";
import { parseAddendum, parseInvoice, generateRemarksAi, type RemarksFacts, type RemarksTone, type RemarksLength } from "./features/selling/api";
import { PriceReductionsPage } from "./features/pricedrops/PriceReductionsPage";
import { allQueue, opPutQueue, queueItemToLeadRow, type QueueItem } from "./features/scrapers/wholesalerQueue";
import { NotificationsPage } from "./features/notifications/NotificationsPage";
import { fetchNotifications, markRead as markNotifsRead, notifRoute, emitNotif } from "./features/notifications/api";
import { BuyersPage } from "./features/buyers/BuyersPage";
import { allBuyers, opPutBuyers, type Buyer } from "./features/buyers/api";
import { cachedMembers, fetchMembers, type Member } from "./features/team/api";
import { TeamPage } from "./features/team/TeamPage";
import { AnalyticsPage } from "./features/analytics/AnalyticsPage";
import { FacebookPage } from "./features/facebook/FacebookPage";
import { IntelPage } from "./features/intel/IntelPage";
import { AgentPage } from "./features/agent/AgentPage";
import { fetchAgentFresh, opPutAgent } from "./features/agent/api";
import { BizFilesPage, type BizLoaders } from "./features/bizfiles/BizFilesPage";
import { fetchBizFolder, fetchBizStatus } from "./features/bizfiles/api";
import { MaterialsPage } from "./features/materials/MaterialsPage";
import { cachedMaterials, fetchMaterials, probeMaterialsWrite, opPutMaterial, opDelMaterial, opPutMatCat, opDelMatCat, lookupMaterialPrice, type MaterialsResult, type MaterialInput } from "./features/materials/api";
import { fetchSow } from "./features/sow/api";
import type { RecOp } from "./lib/outbox";
import {
  CONVERT_MSG, flipCardFor, opCreateDeal, opCreateWholesale, opLeadStageDeal, wholesaleCardFor,
} from "./features/convert/api";
import { isHotReply } from "./features/edits/api";
import { LeadsPage } from "./features/leads/LeadsPage";
import { runEnrichLead } from "./features/leads/enrich";
import { fetchShowingFeed } from "./features/deals/showingtime";
import { TasksPage } from "./features/tasks/TasksPage";
import { InboxPage } from "./features/inbox/InboxPage";
import { DealsPage } from "./features/deals/DealsPage";
import { WholesalePage } from "./features/wholesale/WholesalePage";
import { MessagesPage } from "./features/messages/MessagesPage";
import { DialerPage } from "./features/dialer/DialerPage";
import { sendSms, placeCall } from "./features/dialer/api";
import { SignIn } from "./features/auth/SignIn";
import type { Lead } from "./types";

// Page type + role-aware visibility live in lib/access (single source of truth).

// The mobile "More" sheet used to keep its own copy of this list. It drifted —
// it still listed Dialer (retired 2026-07-24, merged into Inbox) and carried
// Investors and Notifications the sidebar never had. It now renders the same
// NAV_WORKSPACE / NAV_FINANCES / NAV_ADMIN arrays the sidebar does, grouped
// into three segments. See the BottomNav call at the bottom of this file.

export function App() {
  const auth = useMemo(() => new Auth(localStorage), []);
  const [authState, setAuthState] = useState<AuthState>(auth.current);
  const [leads, setLeads] = useState<Lead[] | null>(null); // scraped `properties`
  const [manualLeads, setManualLeads] = useState<Lead[]>([]); // rec_leads (v1 manual/SMS)
  const [edits, setEdits] = useState<EditsMap>(new Map());
  const [tasks, setTasks] = useState<TaskRec[]>([]);
  /** true only once the SERVER's task list has landed. The reminder
   * reconciler must not run against the empty initial state or the cached
   * one: it would re-put reminders that already exist, and a `put` resets
   * status — silently un-completing a reminder you'd already ticked off. */
  const [tasksLoaded, setTasksLoaded] = useState(false);
  /** Same idea on the deals side, and it matters just as much. Deals hydrate
   * from IndexedDB first; if that cache predates a closing date the user
   * entered on another device, the reconciler would see "no date, but four
   * reminders exist", DELETE them, then re-create them seconds later when the
   * live fetch lands — losing whichever ones were already ticked off. Both
   * halves must be server-fresh before anything is derived from them. */
  const [dealsLoaded, setDealsLoaded] = useState(false);
  const [threads, setThreads] = useState<Thread[] | null>(null);
  const [inboxNote, setInboxNote] = useState<string | null>(null);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [dealsReady, setDealsReady] = useState<boolean>(true);
  const [dealsNote, setDealsNote] = useState<string | null>(null);
  // null = STILL PROBING. Telling "unknown" apart from "no" is the whole
  // point: this used to start `false`, and DealsPage swaps every deal write
  // for a no-op while it is false. probeDealsWrite is allowed 12 seconds, so
  // anything Tom did in that window — expenses, stage moves, field edits —
  // was silently discarded. 2026-07-27: "i tried adding twice and it didnt do
  // anything." A control must never be enabled while its handler is inert.
  const [canEditDeals, setCanEditDeals] = useState<boolean | null>(null);
  const [wholesale, setWholesale] = useState<WholesaleDeal[]>([]);
  const [wholesaleNote, setWholesaleNote] = useState<string | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [calGroups, setCalGroups] = useState<CalGroups>({});
  const [sharedViews, setSharedViews] = useState<SharedView[]>([]);
  const [orgSettings, setOrgSettings] = useState<Record<string, unknown>>({});
  // ref twin: ledgerPut (empty deps) diffs against the CURRENT copy
  const orgSettingsRef = useRef<Record<string, unknown>>({});
  orgSettingsRef.current = orgSettings;
  /** when this tab last wrote a settings ledger — a refetch that STARTED
   *  before that must not overwrite the optimistic row (2026-09-13) */
  const lastLedgerWriteRef = useRef(0);
  // Have we ever successfully READ org settings this session?
  //
  // Without this, `{}` means two different things: "you genuinely have no
  // expenses" and "the read hasn't landed / failed". The ledger pages can't
  // tell them apart, so on a failed boot read — first login on a device,
  // Safari private mode blocking IndexedDB, offline at startup — Expenses,
  // Company Expenses and Contractors render fully interactive and EMPTY. Add
  // one row and the whole-array put replaces the real ledger with that single
  // row. The Phase-26 base etag does not save you: it guards concurrent
  // writes, not a read that never happened, so md5(stored) still matches.
  //
  // The contractor ledger carries wire routing and account numbers.
  // Per-USER interface prefs (pinned lead-drawer tabs). Separate from
  // orgSettings, which is org-wide — pinning a tab there would repin it for the
  // whole team. Works before MIGRATION_CRM2_PHASE30_UI_PREFS.sql is applied:
  // loadUiPrefs falls back to localStorage, so the feature is per-device until
  // the column exists and syncs afterwards with no flag to flip.
  const [uiPrefs, setUiPrefs] = useState<UiPrefs>({});
  const [settingsReady, setSettingsReady] = useState(false);
  // ref as well as state: the ledger callbacks below have empty dep arrays and
  // must see the CURRENT value, not the one captured when they were created.
  const settingsReadyRef = useRef(false);
  const markSettingsReady = useCallback(() => {
    settingsReadyRef.current = true;
    setSettingsReady(true);
  }, []);
  const [members, setMembers] = useState<Member[]>([]);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [askAiOpen, setAskAiOpen] = useState(false);
  const [devOpen, setDevOpen] = useState(false);
  // Account menu on the top-right avatar (2026-07-23, Tom: "add the option to
  // log out in the app") — the sidebar's small ⎋ icon was too easy to miss.
  const [acctOpen, setAcctOpen] = useState(false);
  // Rejected-changes inspector (2026-07-19): null = closed, else the dead ops
  // snapshotted when the pill was clicked (stable while the modal is open).
  const [deadOpen, setDeadOpen] = useState<DeadOpView[] | null>(null);
  const [theme, setTheme] = useState<string>(() => {
    try { return localStorage.getItem("crm2_theme") ?? "light"; } catch { return "light"; }
  });
  const [contactsNote, setContactsNote] = useState<string | null>(null);
  const [jumpAddress, setJumpAddress] = useState<string | null>(null);
  const [jumpPeer, setJumpPeer] = useState<string | null>(null);
  const [jumpWholesale, setJumpWholesale] = useState<string | null>(null);
  const [jumpDeal, setJumpDeal] = useState<string | null>(null);
  const [expPrefill, setExpPrefill] = useState<{ desc: string; category: string; property?: string } | null>(null);
  const [syncNote, setSyncNote] = useState<string | null>(null);
  // "waking the backend…" is set by every onWake but nothing cleared it once
  // the slow request landed, so the toast sat there forever (Tom 2026-08-14:
  // "it keeps saying waking the backend and its stuck"). The API was verified
  // healthy at the time — auto-expire the wake note after 12s; a request still
  // genuinely waking re-fires onWake and the note re-arms.
  useEffect(() => {
    if (!syncNote || !/waking the backend/i.test(syncNote)) return;
    const t = setTimeout(() => {
      setSyncNote((cur) => (cur && /waking the backend/i.test(cur) ? null : cur));
    }, 12000);
    return () => clearTimeout(t);
  }, [syncNote]);
  const [box, setBox] = useState<OutboxSnapshot>({ pending: 0, dead: 0, sending: false });
  // v1 reicrm_view: restore the last page + open record so a refresh lands in the
  // same spot. Page is validated here; role-visibility is enforced by the effect
  // below (bounces to the first visible page if this role can't see it).
  const savedView = useMemo(() => loadView(), []);
  const [page, setPage] = useState<Page>(() => {
    // A middle-clicked nav link opens a new tab at #<page> — honor that hash
    // first (so "open in new tab" lands on the right page), else the per-tab
    // saved view, else the default.
    const hash = typeof location !== "undefined" ? location.hash.replace(/^#\/?/, "") : "";
    const fromHash = (NAV_PAGE_IDS as readonly string[]).includes(hash) ? (hash as Page) : null;
    const raw = fromHash ?? (savedView && (NAV_PAGE_IDS as readonly string[]).includes(savedView.page) ? (savedView.page as Page) : "properties");
    // Dialer merged into Inbox (2026-07-24) — heal a restored "dialer" view.
    return raw === "dialer" ? "inbox" : raw;
  });
  // which detail record is open on each page (reported up by the pages), so the
  // saved view can reopen it after a refresh.
  const [openDealKey, setOpenDealKey] = useState<string | null>(savedView?.deal ?? null);
  const [openLeadAddr, setOpenLeadAddr] = useState<string | null>(savedView?.lead ?? null);
  const [openWholesaleKey, setOpenWholesaleKey] = useState<string | null>(savedView?.wholesale ?? null);
  // the open drawer's inner tab (v1 _TT0 parity) — refresh keeps the tab too
  const [openDealTab, setOpenDealTab] = useState<string | null>(savedView?.dealTab ?? null);
  const [openDealSub, setOpenDealSub] = useState<string | null>(savedView?.dealSub ?? null);
  const [openLeadTab, setOpenLeadTab] = useState<string | null>(savedView?.leadTab ?? null);
  // On first load, reopen whatever detail was open (the jump props persist until
  // the page's data loads, so this survives the async deals/leads fetch).
  useEffect(() => {
    if (savedView?.deal) setJumpDeal(savedView.deal);
    if (savedView?.lead) setJumpAddress(savedView.lead);
    if (savedView?.wholesale) setJumpWholesale(savedView.wholesale);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Stale-tab guard: any open tab (Daniel's phone, an old laptop window…)
  // notices a new Cloudflare build and refreshes itself — silently when
  // hidden, with a 2s toast when visible, never while the user is typing.
  useEffect(() => startBuildWatch(), []);
  // client diagnostics trail (2026-09-13) — what THIS tab did, readable server-side
  useEffect(() => {
    diagStart(() => auth.token(), currentIndexAsset());
    diag("boot", { vis: typeof document !== "undefined" ? document.visibilityState : "?" });
    const flush = () => diagFlush();
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") flush(); });
    return () => window.removeEventListener("pagehide", flush);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Push-notification click-through (Tom 2026-07-25): a tap on a push banner
  // lands EXACTLY where it points. Cold start arrives as /?notif=<link>; an
  // already-open window gets {type:"notif-click"} from the service worker.
  useEffect(() => {
    const go = (link: string) => {
      const r = notifRoute(link);
      if (r.kind === "lead") { setJumpAddress(r.address); setPage("properties"); }
      else if (r.kind === "convo") { setJumpPeer(r.phone); setPage("inbox"); }
      else setPage(r.page as typeof page);
    };
    try {
      const q = new URLSearchParams(window.location.search);
      const link = q.get("notif");
      if (link !== null) {
        q.delete("notif");
        const rest = q.toString();
        history.replaceState(null, "", window.location.pathname + (rest ? "?" + rest : "") + window.location.hash);
        if (link) go(link);
      }
    } catch { /* older browsers */ }
    const onMsg = (e: MessageEvent) => {
      const d = e.data as { type?: string; link?: string } | null;
      if (d && d.type === "notif-click") go(typeof d.link === "string" ? d.link : "");
    };
    try { navigator.serviceWorker?.addEventListener("message", onMsg); } catch { /* no SW */ }
    return () => { try { navigator.serviceWorker?.removeEventListener("message", onMsg); } catch { /* ignore */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Persist page + open detail on every change → refresh returns to the same spot.
  useEffect(() => {
    saveView({ page, deal: openDealKey, lead: openLeadAddr, wholesale: openWholesaleKey, dealTab: openDealTab, dealSub: openDealSub, leadTab: openLeadTab });
  }, [page, openDealKey, openLeadAddr, openWholesaleKey, openDealTab, openDealSub, openLeadTab]);
  // Refresh keeps the SCROLL where it was, app-wide ("just stay where it is").
  // Best-effort: remember the last scrolled pane (window, or an inner overflow
  // container identified by its first class name) in the per-tab store, then
  // restore once the reopened view has painted enough content to scroll back —
  // data loads async, so we retry briefly and stand down on any user scroll.
  useEffect(() => {
    try { history.scrollRestoration = "manual"; } catch { /* older browsers */ }
    let t: number | null = null;
    const remember = (cls: string, top: number) => {
      try { sessionStorage.setItem("crm2_scroll", JSON.stringify({ cls, top: Math.round(top) })); } catch { /* private mode */ }
    };
    const onScroll = (e: Event) => {
      if (t !== null) return;
      t = window.setTimeout(() => {
        t = null;
        const el = e.target;
        if (el instanceof HTMLElement && el.classList.length) remember(el.classList[0] ?? "", el.scrollTop);
        else remember("", window.scrollY);
      }, 200);
    };
    document.addEventListener("scroll", onScroll, { capture: true, passive: true });
    let saved: { cls: string; top: number } | null = null;
    try { saved = JSON.parse(sessionStorage.getItem("crm2_scroll") ?? "null") as { cls: string; top: number } | null; } catch { saved = null; }
    let cancelled = false, tries = 0;
    const cancel = () => { cancelled = true; };
    if (saved && typeof saved.top === "number" && saved.top > 0) {
      const want = saved;
      window.addEventListener("wheel", cancel, { once: true, passive: true });
      window.addEventListener("touchstart", cancel, { once: true, passive: true });
      const attempt = () => {
        if (cancelled || tries++ > 50) return;
        if (want.cls) {
          const el = document.getElementsByClassName(want.cls)[0];
          if (el instanceof HTMLElement && el.scrollHeight - el.clientHeight >= want.top - 4) { el.scrollTop = want.top; return; }
        } else if (document.documentElement.scrollHeight - window.innerHeight >= want.top - 4) {
          window.scrollTo(0, want.top);
          return;
        }
        window.setTimeout(attempt, 120);
      };
      window.setTimeout(attempt, 60);
    }
    return () => {
      document.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("wheel", cancel);
      window.removeEventListener("touchstart", cancel);
      if (t !== null) window.clearTimeout(t);
    };
  }, []);
  // Keep the URL hash in step with the current page (replaceState = no history
  // spam, no reload) so it always reflects where you are — that's what makes a
  // middle-click "open in new tab" on a nav link land on the right page.
  useEffect(() => {
    try { history.replaceState(null, "", "#" + page); } catch { /* SSR/blocked */ }
  }, [page]);

  const outbox = useMemo(
    () =>
      new Outbox({ get: idbGet, set: idbSet }, async (ops) => {
        const t = auth.token();
        if (!t) return Promise.reject(new Error("no-token")); // not definitive → retried after repair
        // Ledger puts that carry an intent are rebuilt over the server's
        // CURRENT array right before sending (2026-09-02, Crest Lake: a stale
        // tab's whole-array write erased the expense the other device added).
        // After a success, fold what the server now holds back into local
        // state — re-applying any still-queued delta for that key on top so
        // an edit made during the send isn't visually reverted.
        const foldBack = (merged: Record<string, unknown[]>) => {
          const keys = Object.keys(merged);
          if (!keys.length) return;
          const pend = outboxRef.current.pendingOps();
          setOrgSettings((prev) => {
            const next = { ...prev };
            for (const k of keys) {
              let view = merged[k] as unknown[];
              for (const p of pend) if (p.s === "settings" && p.op === "put" && p.k === k && p.intent) view = applyIntent(view, p.intent);
              next[k] = view;
            }
            return next;
          });
        };
        const ledgerKeys = ops.filter((o) => o.s === "settings").map((o) => o.k);
        try {
          const m = await mergeLedgerOps(t, ops);
          if (ledgerKeys.length) diag("ledger.merge", { keys: ledgerKeys.join(","), merged: Object.entries(m.merged).map(([k, v]) => k + ":" + v.length).join(",") });
          await sendOps(t, m.ops);
          if (ledgerKeys.length) diag("ledger.sent", { keys: ledgerKeys.join(","), n: ops.length });
          foldBack(m.merged);
        } catch (e) {
          if (ledgerKeys.length) diag("ledger.sendfail", { keys: ledgerKeys.join(","), status: (e as { status?: number })?.status ?? null, msg: String((e as Error)?.message ?? e).slice(0, 160), stale: isStaleWrite(e) });
          // SELF-HEAL for whole-array settings writes (expenses/partners/
          // contractors…): a crm2_stale_write reject means our base version
          // tag raced another write. Without this, the op DEAD-LETTERS and the
          // user's edit silently evaporates on the next refetch (Tom: deleted
          // an expense, added one, the add never landed). Retry ONCE: ops with
          // an intent are re-merged over the newest server copy; legacy ops
          // without one get a fresh tag (last write wins, as before).
          if (!isStaleWrite(e)) throw e;
          const m = await mergeLedgerOps(t, ops);
          await fetchSettingsEtags(t);
          await sendOps(t, rebaseLedgerOps(m.ops));
          foldBack(m.merged);
        }
      }),
    [auth],
  );
  const outboxRef = useRef(outbox);
  outboxRef.current = outbox;
  const dealsRef = useRef<Deal[]>([]);
  const tasksRef = useRef<TaskRec[]>([]);
  const editsRef = useRef<EditsMap>(new Map());

  // 1. paint-first: caches to screen before ANY network
  useEffect(() => {
    let alive = true;
    void cachedLeads().then((c) => {
      if (!alive || !c) return;
      setLeads((prev) => prev ?? c);
      setSyncNote((n) => n ?? "showing saved copy");
    });
    void outbox.ready().then(async () => {
      const [ce, ct] = await Promise.all([cachedEdits(), cachedTasks()]);
      if (!alive) return;
      const pending = outbox.pendingOps();
      if (ce) setEdits((prev) => (prev.size ? prev : applyOpsToEdits(ce, pending)));
      if (ct) setTasks((prev) => (prev.length ? prev : applyOpsToTasks(ct, pending)));
    });
    void cachedThreads().then((t) => {
      if (alive && t) setThreads((prev) => prev ?? t);
    });
    void cachedDeals().then((d) => {
      if (alive && d && d.length) setDeals((prev) => (prev.length ? prev : d));
    });
    void cachedWholesale().then((w) => {
      if (alive && w && w.length) setWholesale((prev) => (prev.length ? prev : w));
    });
    void cachedContacts().then((c) => {
      if (alive && c && c.length) setContacts((prev) => (prev.length ? prev : c));
    });
    void cachedCalendar().then((g) => {
      if (alive && g) setCalGroups((prev) => (Object.keys(prev).length ? prev : g));
    });
    void cachedSharedViews().then((v) => {
      if (alive && v && v.length) setSharedViews((prev) => (prev.length ? prev : v));
    });
    void cachedOrgSettings().then((m) => {
      // The IndexedDB copy is a real read — it came from a successful fetch on
      // a previous run. Good enough to unlock writes; the network fill below
      // will replace it moments later.
      if (alive && m) {
        setOrgSettings((prev) => (Object.keys(prev).length ? prev : m));
        markSettingsReady();
      }
    });
    void cachedMembers().then((m) => {
      if (alive && m && m.length) setMembers((prev) => (prev.length ? prev : m));
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. background auth repair + outbox lifecycle
  useEffect(() => {
    const un = auth.subscribe(setAuthState);
    void auth.repair();
    return () => {
      un();
      auth.stop();
    };
  }, [auth]);

  // dark mode (v1 toggleDark parity) — html[data-theme] drives the CSS vars
  useEffect(() => {
    document.documentElement.dataset.theme = theme === "dark" ? "dark" : "";
    try { localStorage.setItem("crm2_theme", theme); } catch { /* private mode */ }
  }, [theme]);
  // MOBILE PERF: flipping the theme used to re-render the whole app tree
  // synchronously while two dozen CSS transition rules animated every node at
  // once — seconds of jank on a phone. Now the DOM attribute flips INSTANTLY
  // (with all transitions suppressed for ~two frames via .notransition) and the
  // React state update rides a low-priority transition — only the two little
  // toggle icons actually depend on it.
  const toggleTheme = useCallback(() => {
    const next = theme === "dark" ? "light" : "dark";
    const root = document.documentElement;
    root.classList.add("notransition");
    root.dataset.theme = next === "dark" ? "dark" : "";
    try { localStorage.setItem("crm2_theme", next); } catch { /* private mode */ }
    window.setTimeout(() => root.classList.remove("notransition"), 120);
    startTransition(() => setTheme(next));
  }, [theme]);
  // MOBILE PERF: switching pages mounts a big page component — done as a React
  // transition, the tap paints immediately (React yields to input) instead of
  // freezing the old page on slow CPUs while the new one builds.
  const goPage = useCallback((p: Page) => startTransition(() => setPage(p)), []);
  // MOBILE PERF: these used to be rebuilt INLINE on every app render (fresh
  // Set/array identities), making the open page reconcile new props whenever
  // anything anywhere changed state. Stable identities = far less re-render work.
  const dealKeySet = useMemo(() => new Set(deals.map((d) => d.natKey)), [deals]);
  const wholesaleKeySet = useMemo(() => new Set(wholesale.map((w) => w.natKey)), [wholesale]);
  const dealAddressList = useMemo(() => [...deals.map((d) => d.address), ...wholesale.map((w) => w.address)], [deals, wholesale]);
  const dealAddressUnique = useMemo(() => [...new Set(dealAddressList)], [dealAddressList]);
  const taskMemberNames = useMemo(
    () => members.map((m) => m.name ?? (m.email.split("@")[0] ?? "").replace(/^./, (c) => c.toUpperCase())),
    [members],
  );
  /** Every string a teammate could have been picked BY → their profile id.
   * The two rosters in this file derive display names differently
   * (`taskMemberNames` falls back to a capitalised email prefix,
   * `activeMemberList` to the whole email), so matching on one of them alone
   * silently notifies nobody when a member has no full_name set. Accepting
   * all forms means whatever the picker offered resolves. */
  const memberIdByName = useMemo(() => {
    const byName = new Map<string, string>();
    for (const m of members) {
      if (!m.id || m.status !== "active") continue;
      const prefix = m.email.split("@")[0] ?? "";
      for (const form of [m.name, m.email, prefix, prefix.replace(/^./, (c) => c.toUpperCase())]) {
        const k = (form ?? "").trim().toLowerCase();
        if (k && !byName.has(k)) byName.set(k, m.id);
      }
    }
    return byName;
  }, [members]);
  const activeMemberList = useMemo(
    () => members.filter((m) => m.status === "active" && m.id).map((m) => ({ id: m.id as string, name: m.name || m.email })),
    [members],
  );

  // ⌘K / Ctrl+K command palette (v1 command-palette parity)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    let lastDead = 0;
    const un = outbox.subscribe((snap) => {
      setBox(snap);
      if (snap.dead > lastDead) {
        for (const d of outbox.deadOps().slice(-(snap.dead - lastDead))) diag("outbox.dead", { s: d.s, op: d.op, k: String(d.k).slice(0, 40), err: d.err ?? null });
      }
      lastDead = snap.dead;
    });
    const online = () => void outbox.drain();
    window.addEventListener("online", online);
    const tick = setInterval(() => void outbox.drain(), 25000);
    return () => {
      un();
      window.removeEventListener("online", online);
      clearInterval(tick);
      outbox.stop();
    };
  }, [outbox]);

  // 3. fresh data once authed — pending outbox ops replayed on top, so a
  //    fetch can never visually revert an edit that hasn't synced yet
  useEffect(() => {
    if (authState.kind !== "authed") return;
    void outbox.drain();
    let alive = true;
    const ctl = new AbortController();
    setSyncNote((n) => n ?? "syncing…");
    const token = authState.session.access_token;
    void (async () => {
      for (let round = 0; alive; round++) {
        try {
          const [r, manual] = await Promise.all([
            fetchLeads(token, leads?.length ?? 0, ctl.signal),
            fetchManualLeads(token, ctl.signal), // additive: never gates the scraped list
          ]);
          if (!alive) return;
          setLeads(r.leads);
          setManualLeads(manual);
          setSyncNote(r.dropped ? `${r.dropped} malformed rows refused` : null);
          break;
        } catch {
          if (!alive) return;
          setSyncNote("server unreachable — retrying, your saved copy stays");
          await new Promise((res) => setTimeout(res, Math.min(30000, 3000 * (round + 1))));
        }
      }
    })();
    void (async () => {
      for (let round = 0; alive; round++) {
        try {
          const [e, t] = await Promise.all([fetchEdits(token, ctl.signal), fetchTasks(token, ctl.signal)]);
          if (!alive) return;
          const pending = outboxRef.current.pendingOps();
          setEdits(applyOpsToEdits(e, pending));
          setTasks(applyOpsToTasks(t.tasks, pending));
          setTasksLoaded(true); // gates the reminder reconciler — see below
          return;
        } catch {
          if (!alive) return;
          await new Promise((res) => setTimeout(res, Math.min(30000, 5000 * (round + 1))));
        }
      }
    })();
    void (async () => {
      for (let round = 0; alive; round++) {
        try {
          const r = await fetchThreads(token, ctl.signal);
          if (!alive) return;
          setThreads(r.threads);
          setInboxNote(r.dropped ? `${r.dropped} malformed messages skipped` : null);
          return;
        } catch {
          if (!alive) return;
          await new Promise((res) => setTimeout(res, Math.min(30000, 5000 * (round + 1))));
        }
      }
    })();
    void probeDealsWrite(token).then((open) => {
      if (alive) setCanEditDeals(open);
    });
    void (async () => {
      for (let round = 0; alive; round++) {
        try {
          const r = await fetchDeals(token, ctl.signal);
          if (!alive) return;
          setDeals(applyOpsToDeals(r.deals, outboxRef.current.pendingOps()));
          setDealsReady(r.ready);
          if (r.ready) setDealsLoaded(true); // gates the reminder reconciler
          setDealsNote(r.dropped ? `${r.dropped} malformed deals skipped` : null);
          return;
        } catch {
          if (!alive) return;
          await new Promise((res) => setTimeout(res, Math.min(30000, 5000 * (round + 1))));
        }
      }
    })();
    void (async () => {
      for (let round = 0; alive; round++) {
        try {
          const r = await fetchWholesale(token, ctl.signal);
          if (!alive) return;
          setWholesale(r.rows);
          setWholesaleNote(r.dropped ? `${r.dropped} malformed rows skipped` : null);
          return;
        } catch {
          if (!alive) return;
          await new Promise((res) => setTimeout(res, Math.min(30000, 5000 * (round + 1))));
        }
      }
    })();
    void (async () => {
      for (let round = 0; alive; round++) {
        try {
          const r = await fetchContacts(token, ctl.signal);
          if (!alive) return;
          setContacts(r.rows);
          setContactsNote(r.dropped ? `${r.dropped} malformed contacts skipped` : null);
          return;
        } catch {
          if (!alive) return;
          await new Promise((res) => setTimeout(res, Math.min(30000, 5000 * (round + 1))));
        }
      }
    })();
    void (async () => {
      for (let round = 0; alive; round++) {
        try {
          const r = await fetchCalendar(token, ctl.signal);
          if (!alive) return;
          setCalGroups(r.groups);
          return;
        } catch {
          if (!alive) return;
          await new Promise((res) => setTimeout(res, Math.min(30000, 5000 * (round + 1))));
        }
      }
    })();
    void (async () => {
      for (let round = 0; alive; round++) {
        try {
          const v = await fetchSharedViews(token, ctl.signal);
          if (!alive) return;
          setSharedViews(v);
          return;
        } catch {
          if (!alive) return;
          await new Promise((res) => setTimeout(res, Math.min(30000, 5000 * (round + 1))));
        }
      }
    })();
    let settingsAt = 0;
    void (async () => {
      for (let round = 0; alive; round++) {
        try {
          const m = await fetchOrgSettings(token, ctl.signal);
          if (!alive) return;
          setOrgSettings(m);
          settingsAt = Date.now();
          markSettingsReady();
          return;
        } catch {
          if (!alive) return;
          await new Promise((res) => setTimeout(res, Math.min(30000, 5000 * (round + 1))));
        }
      }
    })();
    // Settings were read ONCE per session. A tab left open holds that copy
    // for days, and the expense/contractor/partner ledgers it then writes are
    // built from it (2026-09-02, Crest Lake). Re-pull on focus when the copy
    // is older than 90s — skipped while a settings write of ours is still
    // queued, so the refetch can't visually revert it before it lands.
    let refreshing = false;
    const refreshSettings = () => {
      if (!alive || refreshing || document.visibilityState !== "visible") return;
      if (Date.now() - settingsAt < 90_000) return;
      if (outboxRef.current.pendingOps().some((o) => o.s === "settings")) return;
      refreshing = true;
      const startedAt = Date.now();
      diag("settings.refetch", { age: Math.round((startedAt - settingsAt) / 1000) });
      void fetchOrgSettings(token, ctl.signal)
        .then((m) => {
          if (!alive) return;
          // a ledger write landed while this fetch was in flight: the copy we
          // hold is older than the user's edit — drop it, never the edit
          if (lastLedgerWriteRef.current >= startedAt || outboxRef.current.pendingOps().some((o) => o.s === "settings")) {
            diag("settings.refetch.skipped", { why: "write-during-fetch" });
            return;
          }
          setOrgSettings(m);
          settingsAt = Date.now();
          markSettingsReady();
        })
        .catch(() => { /* next focus tries again */ })
        .finally(() => { refreshing = false; });
    };
    document.addEventListener("visibilitychange", refreshSettings);
    window.addEventListener("focus", refreshSettings);
    // Per-user UI prefs. One shot, no retry loop: a missing pinned-tab list is
    // a cosmetic default, not a data gap, and loadUiPrefs already falls back to
    // the local copy on any failure.
    void loadUiPrefs(token, sessionUser?.id ?? null).then((p) => { if (alive) setUiPrefs(p); });
    void (async () => {
      for (let round = 0; alive; round++) {
        try {
          const m = await fetchMembers(token, ctl.signal);
          if (!alive) return;
          setMembers(m);
          return;
        } catch {
          if (!alive) return;
          await new Promise((res) => setTimeout(res, Math.min(30000, 5000 * (round + 1))));
        }
      }
    })();
    return () => {
      alive = false;
      ctl.abort();
      document.removeEventListener("visibilitychange", refreshSettings);
      window.removeEventListener("focus", refreshSettings);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.kind]);

  // ---- edit actions: optimistic paint + queue --------------------------------
  const changeStatus = useCallback((address: string, status: string) => {
    const k = natKey(address);
    setEdits((prev) => new Map(prev).set(k, { ...(prev.get(k) ?? {}), qual: status, stage: status }));
    void outboxRef.current.enqueue(opSetStatus(address, status));
  }, []);

  const changeNotes = useCallback((address: string, notes: string) => {
    const k = natKey(address);
    setEdits((prev) => new Map(prev).set(k, { ...(prev.get(k) ?? {}), notes }));
    void outboxRef.current.enqueue(opSetNotes(address, notes));
  }, []);

  const changeArv = useCallback((address: string, kind: "listedArv" | "compedArv", value: number | null) => {
    const k = natKey(address);
    setEdits((prev) => {
      const op = opSetArv(address, kind, value, prev.get(k));
      const next = new Map(prev);
      const merged = { ...(prev.get(k) ?? {}) };
      for (const [key, v] of Object.entries(op.d ?? {})) {
        if (v === null) delete merged[key];
        else merged[key] = v;
      }
      next.set(k, merged);
      void outboxRef.current.enqueue(op);
      return next;
    });
  }, []);

  const bulkStatus = useCallback((addresses: string[], status: string) => {
    setEdits((prev) => {
      const next = new Map(prev);
      for (const a of addresses) {
        const k = natKey(a);
        next.set(k, { ...(next.get(k) ?? {}), qual: status, stage: status });
      }
      return next;
    });
    for (const a of addresses) void outboxRef.current.enqueue(opSetStatus(a, status));
  }, []);

  // v1 batchDelete parity: tombstone every address (one tombs op — v1 and v2
  // both hide tombstoned leads), atomically dismiss each scraped row so the
  // scrapers can't resurrect it, and drop them from the list optimistically.
  const bulkDelete = useCallback((chosen: Lead[]) => {
    if (!chosen.length) return;
    const addrs = chosen.map((l) => l.address);
    void outboxRef.current.enqueue(opTombsAddLeads(addrs));
    // manual leads (rec_leads) soft-delete through the door; scraped rows
    // (properties) go through the atomic dismiss RPC — split by id prefix.
    const manual = chosen.filter((l) => isManualLead(l.id));
    const scraped = chosen.filter((l) => !isManualLead(l.id));
    for (const l of manual) void outboxRef.current.enqueue(opDelLead(l.address));
    const goneIds = new Set(chosen.map((c) => c.id));
    setLeads((prev) => (prev ? prev.filter((l) => !goneIds.has(l.id)) : prev));
    setManualLeads((prev) => prev.filter((l) => !goneIds.has(l.id)));
    const token = auth.token();
    if (token && scraped.length) {
      void (async () => {
        let failed = 0;
        for (const l of scraped) {
          try {
            const ok = await dismissProperty(token, l.id);
            if (!ok) failed++;
          } catch {
            failed++;
          }
        }
        if (failed) setSyncNote(`${failed} of ${scraped.length} deletions did not confirm — they may reappear; tell Claude`);
        else setSyncNote(`${chosen.length} deleted`);
      })();
    } else {
      setSyncNote(`${chosen.length} deleted`);
    }
  }, [auth]);

  // Add Property (v1 manual add). Builds v1's exact rec_leads row, writes it
  // through the PHASE 16 `leads` door section, sets the chosen status via the
  // shared edits map (so v1 shows it too), and paints it optimistically. The
  // modal's Create button is Phase-B-gated, so this fires only once live.
  const addProperty = useCallback((payload: { form: AddPropertyForm; zillowUrl: string | null }) => {
    const { form, zillowUrl } = payload;
    const address = fullAddress(form);
    if (!address) return;
    const row = buildManualLeadRow(form, { nowMs: Date.now(), zillowUrl });
    void outboxRef.current.enqueue(opAddLead(row));
    if (form.status.trim()) {
      setEdits((prev) => new Map(prev).set(natKey(address), { ...(prev.get(natKey(address)) ?? {}), qual: form.status.trim(), stage: form.status.trim() }));
      void outboxRef.current.enqueue(opSetStatus(address, form.status.trim()));
    }
    const optimistic = narrowManualLead({ nat_key: manualKey(address), address, source: form.source || "Manual", data: row, created_at: new Date().toISOString() });
    if (optimistic) setManualLeads((prev) => [optimistic, ...prev.filter((l) => l.id !== optimistic.id)]);
    setSyncNote(`Added ${address}`);
  }, []);

  // Wholesale edits (Phase 9): data patches, optimistic + queued.
  const editWholesale = useCallback((natKey: string, patch: Partial<{ stage: string; ucp: string; buyer: string; fee: string; seller: string; status: string }>) => {
    setWholesale((prev) => prev.map((w) => (w.natKey === natKey ? { ...w, ...patch } : w)));
    void outboxRef.current.enqueue(opEditWholesale(natKey, patch));
  }, []);
  // wl_*/wm_* Lead-Details / Listing fields (v1 dispo-modals ef()). Patch rides
  // the wholesale door into `data`; narrowWholesale overlays it back over the
  // migrated `fields`, so the optimistic merge here mirrors that (data wins).
  const editWholesaleField = useCallback((natKey: string, key: string, value: string) => {
    setWholesale((prev) => prev.map((w) => {
      if (w.natKey !== natKey) return w;
      const fields = { ...w.fields, [key]: value };
      const wl = typeof fields["wl_asking"] === "string" ? (fields["wl_asking"] as string) : "";
      const wm = typeof fields["wm_ask"] === "string" ? (fields["wm_ask"] as string) : "";
      const asking = wl || wm || w.asking;
      return { ...w, fields, asking };
    }));
    void outboxRef.current.enqueue(opEditWholesaleField(natKey, key, value));
  }, []);
  // Dispo sub-records (v1 dispo-modals.js): whole-array replace of one list on
  // the wholesale record — the door replaces array keys wholesale.
  const saveDispo = useCallback((natKey: string, kind: DispoKind, list: unknown[]) => {
    setWholesale((prev) => prev.map((w) => (w.natKey === natKey ? { ...w, [kind]: list } : w)));
    void outboxRef.current.enqueue(opPutDispo(natKey, kind, list));
  }, []);
  // Wholesale collab lists (v1: contacts / team / chat / comms) — same whole-array
  // replace through the Phase 9 door; optimistic + queued.
  const saveWtList = useCallback((natKey: string, key: WtListKey, list: unknown[]) => {
    setWholesale((prev) => prev.map((w) => (w.natKey === natKey ? { ...w, [key]: list } : w)));
    void outboxRef.current.enqueue(opPutWtList(natKey, key, list));
  }, []);
  const saveWtNotes = useCallback((natKey: string, notes: string) => {
    setWholesale((prev) => prev.map((w) => (w.natKey === natKey ? { ...w, notes } : w)));
    void outboxRef.current.enqueue(opEditWholesale(natKey, { notes }));
  }, []);
  const saveWtFlyer = useCallback((natKey: string, flyer: WtFlyer) => {
    setWholesale((prev) => prev.map((w) => (w.natKey === natKey ? { ...w, flyer } : w)));
    void outboxRef.current.enqueue(opSaveWtFlyer(natKey, flyer));
  }, []);

  // Contacts (Phase 11 door): save / delete+tombstone / merge — optimistic,
  // durable through the outbox, visible in v1 on its next poll.
  const saveContact = useCallback((rec: Record<string, unknown>, oldKey: string | null) => {
    const built = contactOpsForSave(rec, oldKey);
    if (!built) return;
    const optimistic = narrowContact({ nat_key: built.newKey, data: rec, updated_at: new Date().toISOString() });
    setContacts((prev) => {
      const rest = prev.filter((c) => c.natKey !== built.newKey && c.natKey !== oldKey);
      return optimistic ? [optimistic, ...rest] : rest;
    });
    for (const op of built.ops) void outboxRef.current.enqueue(op);
  }, []);
  const deleteContact = useCallback((c: Contact) => {
    setContacts((prev) => prev.filter((x) => x.natKey !== c.natKey));
    for (const op of contactOpsForDelete(c)) void outboxRef.current.enqueue(op);
  }, []);
  const mergeContacts = useCallback((keeper: Contact, losers: Contact[]) => {
    const built = contactOpsForMerge(keeper, losers);
    if (!built) return;
    const gone = new Set([keeper.natKey, ...losers.map((l) => l.natKey)]);
    const merged = narrowContact({
      nat_key: (contactOpsForSave(built.merged, null)?.newKey) ?? keeper.natKey,
      data: built.merged,
      updated_at: new Date().toISOString(),
    });
    setContacts((prev) => {
      const rest = prev.filter((x) => !gone.has(x.natKey));
      return merged ? [merged, ...rest] : rest;
    });
    for (const op of built.ops) void outboxRef.current.enqueue(op);
  }, []);

  // Shared saved views (Phase 10): the same custom views v1 lists.
  const saveSharedView = useCallback((name: string, spec: ViewSpec) => {
    setSharedViews((prev) => {
      const v: SharedView = { name, spec, hasCircles: !!spec.circles?.length };
      return [...prev.filter((x) => x.name !== name), v];
    });
    void outboxRef.current.enqueue(opPutSharedView(name, spec));
  }, []);
  const deleteSharedView = useCallback((name: string) => {
    setSharedViews((prev) => prev.filter((x) => x.name !== name));
    void outboxRef.current.enqueue(opDelSharedView(name));
  }, []);

  // Comps tool (Phase 12): persist {filters, saved} via the rep patch so
  // unstars really delete; ARV applies v1's cmpApplyArv contract (edits.arv).
  const persistCompsTool = useCallback((address: string, state: { filters: CompsFilters; saved: Record<string, SavedComp> }) => {
    const op = opPersistCompsTool(address, state);
    setEdits((prev) => new Map(prev).set(op.k, { ...(prev.get(op.k) ?? {}), compsTool: { filters: state.filters, saved: state.saved } }));
    void outboxRef.current.enqueue(op);
  }, []);
  // Itemized rehab sheet (v1 rehabCalc): store the object edit + mirror the
  // total into the canonical `rehab` edit so the Deal Engine / offer sheet /
  // Net-ROI reprice, optimistically and through the door.
  const saveRehabCalc = useCallback((address: string, state: RehabCalcState, total: number) => {
    const op = opSaveRehabCalc(address, state, total);
    setEdits((prev) => {
      const merged = { ...(prev.get(op.k) ?? {}) };
      for (const [key, v] of Object.entries(op.d ?? {})) merged[key] = v;
      return new Map(prev).set(op.k, merged);
    });
    void outboxRef.current.enqueue(op);
  }, []);
  // Seller contact card (v1 saveSeller/clearSeller): object edit on lead_fields,
  // optimistic + queued. Feeds the deal drawer's Contacts (Parity 5b).
  // RENAME A LEAD (Tom 2026-08-11): the address is the identity key across
  // every table, so the server re-keys them all; the client then does a FULL
  // reload — its local rec cache holds thousands of rows under the old key,
  // and a reload is the one honest way to rebuild every index at once.
  // Renames are rare; correctness beats seamlessness here. The outbox is
  // flushed first so no queued edit is lost, and the saved view is pointed
  // at the new address so the drawer reopens on the renamed lead.
  const renameLead = useCallback(async (oldAddress: string, newAddress: string) => {
    await outboxRef.current.drain().catch(() => undefined);
    await backendFetch(auth.token(), "/api/records/rename-lead", {
      method: "POST",
      body: { old_address: oldAddress, new_address: newAddress },
      onWake: () => setSyncNote("waking the backend…"),
    });
    setOpenLeadAddr(newAddress);   // persisted by the saved-view effect
    await new Promise((res) => setTimeout(res, 150));
    window.location.reload();
  }, [auth]);

  const saveSeller = useCallback((address: string, seller: SellerContact | null) => {
    const op = seller ? opSaveSeller(address, seller) : opClearSeller(address);
    setEdits((prev) => {
      const merged = { ...(prev.get(op.k) ?? {}) };
      for (const [key, v] of Object.entries(op.d ?? {})) merged[key] = v;
      return new Map(prev).set(op.k, merged);
    });
    void outboxRef.current.enqueue(op);
  }, []);
  // Media links (v1 edits.mediaLinks): whole-array replace on lead_fields (rep),
  // optimistic + queued — same object-edit path as the seller card.
  const saveMediaLinks = useCallback((address: string, links: MediaLink[]) => {
    const op = opSaveMediaLinks(address, links);
    setEdits((prev) => new Map(prev).set(op.k, { ...(prev.get(op.k) ?? {}), mediaLinks: links }));
    void outboxRef.current.enqueue(op);
  }, []);
  const applyCompsArv = useCallback((address: string, arv: number) => {
    const op = opApplyCompsArv(address, arv);
    setEdits((prev) => {
      const merged = { ...(prev.get(op.k) ?? {}) };
      for (const [key, v] of Object.entries(op.d ?? {})) merged[key] = v;
      return new Map(prev).set(op.k, merged);
    });
    void outboxRef.current.enqueue(op);
  }, []);
  const compsSearch = useCallback(
    (address: string, f: CompsFilters, onWake: () => void) => searchComps(auth.token(), address, f, { onWake }),
    [auth],
  );
  const compsPhotos = useCallback((c: CompRow) => fetchCompPhotos(auth.token(), c), [auth]);

  // ARV Engine (v1 AI Estimator): run against the same backend; the result
  // (with per-comp included flags) lives at edits.arvResult — shared with v1.
  const runArv = useCallback(
    (address: string, sqft: number, beds: number | null, onWake: () => void) =>
      runArvEstimate(auth.token(), address, sqft, beds, { onWake }),
    [auth],
  );
  const saveArvResult = useCallback((address: string, result: ArvResult) => {
    const op = opSaveArvResult(address, result);
    setEdits((prev) => new Map(prev).set(op.k, { ...(prev.get(op.k) ?? {}), arvResult: result as unknown as Record<string, unknown> }));
    void outboxRef.current.enqueue(op);
  }, []);

  // Emails tab loaders: org + admin resolved from MY profiles row (RLS-scoped),
  // user id from the session — the same identifiers v1 sends the mail routes.
  const sessionUser =
    authState.kind === "authed" || authState.kind === "repairing" ? authState.session.user : null;
  // Every title company + dispo contact ever used, across leads and deals —
  // feeds the "pick from past" selects (Tom 2026-08-31).
  const pastContacts = useMemo(() => collectPastContacts(edits, deals), [edits, deals]);

  const myProfile = useMemo(() => {
    const email = sessionUser?.email?.toLowerCase();
    return email ? members.find((m) => m.email.toLowerCase() === email) ?? null : null;
  }, [members, sessionUser]);
  const role = myProfile?.role ?? null; // admin/manager/member (Tom = admin) → full

  // Per-user page restrictions (PERMS blob). Empty for admins + when unset, so
  // this is inert until an admin hides a page for someone in Settings → Users.
  const hiddenPages = useMemo(
    () => hiddenPagesFor(orgSettings["PERMS"], myProfile?.id, role),
    [orgSettings, myProfile, role],
  );
  // Sensitive lead-detail field groups hidden from THIS user (v1 canSeeField).
  const hiddenFields = useMemo(
    () => hiddenFieldsFor(orgSettings["PERMS"], myProfile?.id, role),
    [orgSettings, myProfile, role],
  );
  // Pages this user has VIEW (read-only) access to — v1 None/View/Edit middle
  // state. The app downgrades each such page's edit capability below.
  const readOnlyPages = useMemo(
    () => readOnlyPagesFor(orgSettings["PERMS"], myProfile?.id, role),
    [orgSettings, myProfile, role],
  );
  const pageEditable = useCallback((p: Page) => !readOnlyPages.has(p), [readOnlyPages]);
  // Gate a handler to a no-op when a page is VIEW-only, so passing it to a page
  // component makes that page's edits inert (v1 None/View/Edit "View" level).
  const gateEdit = useCallback(
    <F,>(editable: boolean, fn: F): F => (editable ? fn : (((..._a: unknown[]) => undefined) as unknown as F)),
    [],
  );
  // Per-user capability gate (v1 canDo): delete / convert / export / financials /
  // manageTemplates / manageScrapers. The FULL roles (admin/manager/member) always
  // pass — so the current all-manager/member team is never affected — and only the
  // named restricted roles are held to the PERMS allow-list. Inert until such a
  // role is provisioned, exactly like the page/field hide lists above.
  const canAct = useCallback(
    (action: string) => canDoAction(orgSettings["PERMS"], myProfile?.id, role, action),
    [orgSettings, myProfile, role],
  );
  const canSee = useCallback((p: Page) => canSeePageFor(role, p, hiddenPages), [role, hiddenPages]);

  // Guard: if the current page isn't visible (role gate OR a per-user hide),
  // bounce to the first page they can see. Dashboard is never hidden, so this
  // always resolves. Fires for named restricted roles + PERMS-restricted users.
  useEffect(() => {
    if (!canSee(page)) setPage(firstVisiblePage(role, hiddenPages));
  }, [role, page, hiddenPages, canSee]);
  const mailLoaders = useMemo<MailLoaders>(
    () => ({
      accounts: () =>
        fetchMailAccounts(auth.token(), myProfile?.orgId ?? "", sessionUser?.id ?? "", myProfile?.role === "admin"),
      threads: (accountId, q) => fetchLeadThreads(auth.token(), accountId, q),
      log: (leadKey) => fetchLeadLog(auth.token(), leadKey, myProfile?.orgId ?? ""),
      body: (accountId, id) => fetchMailBody(auth.token(), accountId, id),
      userId: () => sessionUser?.id ?? null,
      send: (p) => sendMail(auth.token(), myProfile?.orgId ?? "", p),
    }),
    [auth, myProfile, sessionUser],
  );

  // Drive (deal drawer) + Permits AI search — same backend, read/enrich only.
  const driveLoaders = useMemo<DriveLoaders>(
    () => ({
      files: (address, category, dispo) => fetchDriveFiles(auth.token(), address, category, dispo),
      categories: (address, dispo) => fetchDriveCategories(auth.token(), address, dispo),
      upload: (address, category, dispo, file) => uploadDriveFile(auth.token(), { address, category, dispo, file }),
      // in-app preview without Google's viewer (2026-09-24)
      blob: (fileId, signal) => fetchDriveBlob(auth.token(), fileId, signal),
      pdfPages: (fileId, signal) => fetchPdfPages(auth.token(), fileId, signal),
      pdfPage: (fileId, n, width, signal) => fetchPdfPage(auth.token(), fileId, n, width, signal),
    }),
    [auth],
  );
  const aiPermitSearch = useCallback(
    (address: string, county: string, leadId: string) =>
      runAiPermitSearch(auth.token(), address, county, leadId, myProfile?.orgId ?? ""),
    [auth, myProfile],
  );
  // ShowingTime feed (go-live item 4): pull the address-scoped events through
  // the backend. The feed URL is set in Settings → Integrations (SHOWINGTIME_FEED,
  // editable here and accepted by the write door — it is NOT a v1-only setting).
  const showingFeed = useCallback(
    (feedUrl: string, address: string) => fetchShowingFeed(auth.token(), feedUrl, address),
    [auth],
  );
  // SMS + call from the lead drawer (go-live items 5/6): the same Quo routes
  // the Messages page fires; after a confirmed send, refresh the comms so the
  // new message appears in the thread (and the Inbox) right away.
  const sendLeadSms = useCallback(async (to: string, text: string, leadKey: string, name?: string, idemKey?: string) => {
    await sendSms(auth.token(), { org_id: myProfile?.orgId ?? "", to, text, lead_key: leadKey, ...(name ? { contact_name: name } : {}), ...(idemKey ? { idem_key: idemKey } : {}) });
    try {
      const t = auth.token();
      if (t) {
        const r = await fetchThreads(t);
        setThreads(r.threads);
      }
    } catch { /* the inbox poll catches up */ }
  }, [auth, myProfile]);
  const callLead = useCallback(async (to: string, leadKey: string, name?: string) => {
    await placeCall(auth.token(), { org_id: myProfile?.orgId ?? "", to, lead_key: leadKey, ...(name ? { contact_name: name } : {}) });
  }, [auth, myProfile]);
  // Enrich (v1 recheckMarket): market/county/flood pulls → lead_fields edits
  // through the door; the server stamps the properties row (zest/tax/history),
  // so a `stamped` result refetches the list to show what landed.
  const leadsLenRef = useRef(0);
  useEffect(() => { leadsLenRef.current = leads?.length ?? 0; }, [leads]);
  // LIVE NEW-LEAD PICKUP (2026-09-14, Tom: "Why it doesnt create leads in the
  // system from the number that sent deals now"). The leads WERE created a
  // second after each text; the list on his screen was the boot-time copy,
  // so nothing new ever appeared until a reload. Once a minute while the tab
  // is visible (and on refocus, ≥20s apart) pull only the rows newer than
  // the newest one we hold — one small page, never the full list — and
  // prepend them. Rows already on screen are never touched.
  const leadsNewestRef = useRef<number | null>(null); // null = boot sync hasn't landed
  useEffect(() => { leadsNewestRef.current = leads ? newestLeadMs(leads) : null; }, [leads]);
  useEffect(() => {
    if (authState.kind !== "authed") return;
    let alive = true;
    let busy = false;
    let lastAt = 0;
    const tick = (minGap: number) => {
      if (!alive || busy || document.visibilityState !== "visible") return;
      const newest = leadsNewestRef.current;
      if (newest === null) return;
      if (Date.now() - lastAt < minGap) return;
      const t = auth.token();
      if (!t) return;
      busy = true;
      lastAt = Date.now();
      void fetchNewLeads(t, newest)
        .then((fresh) => {
          if (!alive || fresh.length === 0) return;
          setLeads((cur) => {
            if (!cur) return cur;
            const next = mergeNewLeads(cur, fresh);
            if (next !== cur) diag("leads.live", { added: next.length - cur.length });
            return next;
          });
        })
        .catch(() => { /* next tick tries again */ })
        .finally(() => { busy = false; });
    };
    const id = setInterval(() => tick(0), 60_000);
    const onFocus = () => tick(20_000);
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      alive = false;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
  }, [authState.kind, auth]);
  const refetchLeads = useCallback(async () => {
    const t = auth.token();
    if (!t) return;
    try {
      const r = await fetchLeads(t, leadsLenRef.current);
      setLeads(r.leads);
    } catch { /* keep what's on screen; the next boot sync catches up */ }
  }, [auth]);
  // Emails page (mailbox reader) — reads only; send/connect are Phase B.
  const mailboxLoaders = useMemo<MailboxLoaders>(
    () => ({
      accounts: () =>
        fetchMailAccounts(auth.token(), myProfile?.orgId ?? "", sessionUser?.id ?? "", myProfile?.role === "admin"),
      inbox: (accountId) => fetchMailInbox(auth.token(), accountId),
      message: (accountId, id) => fetchMailMessageFull(auth.token(), accountId, id),
      // go-live item 2 (2026-07-23): compose + reply ride the SAME send route
      // the lead threads already fire — one send path, one behavior.
      send: (p) => sendMail(auth.token(), myProfile?.orgId ?? "", p),
    }),
    [auth, myProfile, sessionUser],
  );
  // SOW (Phase 15): reads from the scope tables; writes queue door ops.
  const sowFetch = useCallback((dealKey: string) => fetchSow(auth.token() ?? "", dealKey), [auth]);
  const sowOps = useCallback(
    (ops: RecOp[]) => {
      for (const op of ops) void outboxRef.current.enqueue(op);
    },
    [],
  );
  // SOW Save button (real, server-confirmed): flush the outbox and report
  // whether every queued op landed. No fake "Saved ✓" — this actually drains.
  const sowForceSave = useCallback(async (): Promise<boolean> => {
    await outboxRef.current.drain();
    return outboxRef.current.pendingOps().length === 0;
  }, []);
  // Materials catalog (read-only Phase A) — cache-first, refresh on open.
  const [materials, setMaterials] = useState<MaterialsResult>({ rows: [], cats: [], ready: true });
  const [materialsCanEdit, setMaterialsCanEdit] = useState(false);
  const materialsLoaded = useRef(false);
  const refreshMaterials = useCallback(() => {
    if (!materialsLoaded.current) {
      materialsLoaded.current = true;
      void cachedMaterials().then((c) => {
        if (c) setMaterials((prev) => (prev.rows.length ? prev : c));
      });
    }
    const t = auth.token();
    if (t) {
      // Keep the cached rows when the read fails. Overwriting good IndexedDB-
      // backed state with an errored empty result turned a timeout into
      // "0 materials" — and the SOW material picker reads the same state.
      void fetchMaterials(t)
        .then((r) => setMaterials((prev) => (r.error && prev.rows.length ? { ...prev, error: r.error } : r)))
        .catch(() => {});
      void probeMaterialsWrite(t).then(setMaterialsCanEdit).catch(() => setMaterialsCanEdit(false));
    }
  }, [auth]);
  // Materials CRUD (Phase 24 door): optimistic local update + queued op. Stays
  // inert until the probe passes (migration run + core routes the section).
  const putMaterial = useCallback((id: string, input: MaterialInput, keepHistory: { date: string; price: number }[]) => {
    const op = opPutMaterial(id, input, keepHistory);
    setMaterials((prev) => {
      const existing = prev.rows.find((m) => m.natKey === op.k);
      const rec = {
        natKey: op.k, name: input.name.trim(), categoryId: input.categoryId, price: input.price || null,
        unit: input.unit || null, supplier: input.supplier || null, vendor: input.vendor || null, sku: input.sku || null,
        internet: input.internet || null, notes: input.notes || null, thumbnail: input.thumbnail || null,
        website: input.website ? (/^https?:\/\//i.test(input.website) ? input.website : "https://" + input.website) : null,
        priceTrend: existing?.priceTrend ?? null, history: keepHistory,
      };
      const rows = existing ? prev.rows.map((m) => (m.natKey === op.k ? rec : m)) : [rec, ...prev.rows];
      return { ...prev, rows };
    });
    void outboxRef.current.enqueue(op);
  }, []);
  const deleteMaterial = useCallback((natKeyId: string) => {
    const op = opDelMaterial(natKeyId);
    setMaterials((prev) => ({ ...prev, rows: prev.rows.filter((m) => m.natKey !== op.k) }));
    void outboxRef.current.enqueue(op);
  }, []);
  const putMatCat = useCallback((id: string, name: string) => {
    const op = opPutMatCat(id, name);
    setMaterials((prev) => ({ ...prev, cats: prev.cats.some((c) => c.id === id) ? prev.cats.map((c) => (c.id === id ? { id, name: name.trim() } : c)) : [...prev.cats, { id, name: name.trim() }] }));
    void outboxRef.current.enqueue(op);
  }, []);
  const deleteMatCat = useCallback((id: string) => {
    const op = opDelMatCat(id);
    setMaterials((prev) => ({ rows: prev.rows.filter((m) => m.categoryId !== id), cats: prev.cats.filter((c) => c.id !== id), ready: prev.ready }));
    void outboxRef.current.enqueue(op);
  }, []);
  // Business Files (settings-level Drive browser) — read-only until Phase B.
  const bizLoaders = useMemo<BizLoaders>(
    () => ({
      status: () => fetchBizStatus(auth.token() ?? ""),
      folder: (folderId, onWake) => fetchBizFolder(auth.token() ?? "", folderId, onWake),
    }),
    [auth],
  );
  // Scrapers (Phase 20 read policies + Phase 21 job door): global monitoring
  // tables + org-scoped scraper_jobs read via PostgREST; job writes go through
  // the crm2_scraper_job_apply door. Memoized so the page loads once per nav.
  const scraperLoaders = useMemo(
    () => ({
      health: () => fetchScraperHealth(auth.token() ?? ""),
      stats: () => fetchFetchStats(auth.token() ?? ""),
      jobs: () => fetchScraperJobs(auth.token() ?? ""),
      save: (job: { id?: string; source: ScraperSource; name: string; config: Record<string, unknown>; enabled: boolean }) => saveScraperJob(auth.token() ?? "", job),
      toggle: (id: string, enabled: boolean) => toggleScraperJob(auth.token() ?? "", id, enabled),
      del: (id: string) => deleteScraperJob(auth.token() ?? "", id),
      pauseAll: (enabled: boolean) => pauseAllScrapers(auth.token() ?? "", enabled),
    }),
    [auth],
  );
  // Deal Inbox (v1 deal-inbox.js): server AI parser on the Render backend. Reads
  // (status) are safe; parse/scan CREATE real leads and are gated at the page by
  // liveFire `dealInbox`. Org comes from the profile like the mail loaders.
  const dealInboxLoaders = useMemo(
    () => ({
      status: () => fetchInboxStatus(auth.token()),
      parseText: (body: string, source: "Manual" | "Facebook", sender?: string) => inboxParseText(auth.token(), myProfile?.orgId ?? "", body, source, sender),
      parseImage: (b64: string, mime: string, poster?: string) => inboxParseImage(auth.token(), myProfile?.orgId ?? "", b64, mime, poster),
      scan: () => scanGmail(auth.token(), myProfile?.orgId ?? ""),
    }),
    [auth, myProfile],
  );

  // Text Agent writes (Phase 13): the page mutates a fresh copy surgically;
  // this paints it and queues the whole-AGENT settings put.
  const agentFetchFresh = useCallback(() => fetchAgentFresh(auth.token()), [auth]);
  const putAgent = useCallback((blob: Record<string, unknown>) => {
    setOrgSettings((prev) => ({ ...prev, AGENT: blob }));
    void outboxRef.current.enqueue(opPutAgent(blob));
  }, []);

  // Settings config writes (Automation toggles, ROI defaults) — same durable
  // path as putAgent: optimistic local update + a settings put in the outbox.
  // The Settings UI only calls this when liveFire `settingsEdit` is on (Phase B);
  // the door whitelists exactly these keys (PHASE17).
  const putSetting = useCallback((key: WritableSettingKey, value: Record<string, unknown>) => {
    setOrgSettings((prev) => ({ ...prev, [key]: value }));
    void outboxRef.current.enqueue(opPutSetting(key, value));
  }, []);

  // Per-property ROI overrides (NR_BY). v1 owned this: you typed a number into
  // the Net ROI calculator and v1 persisted it against the address. v2 read
  // NR_BY everywhere (est-money, the deal rehab chain, investor matching) but
  // could only ever write {} to reset — so retiring v1 would have left the 11
  // existing overrides working and no way to add or change one.
  //
  // MERGES into the existing blob. Settings puts replace the key whole, so
  // writing just this address would delete every other property's override —
  // the same trap the AUTOMATION toggle fell into.
  const saveUiPrefs = useCallback((next: UiPrefs) => {
    setUiPrefs(next);                       // optimistic: pinning must feel instant
    void saveUiPrefsRemote(auth.token(), sessionUser?.id ?? null, next);
  }, [auth, sessionUser]);

  const saveNrOverride = useCallback((address: string, vals: Record<string, number>) => {
    const key = String(address || "").trim();
    if (!key) return;
    setOrgSettings((prev) => {
      const raw = prev["NR_BY"];
      const base = raw !== null && typeof raw === "object" && !Array.isArray(raw)
        ? { ...(raw as Record<string, unknown>) } : {};
      // An empty override is a removal, not an empty object — otherwise
      // clearing every field would leave a husk that still counts as an
      // override in Settings' "N properties overridden".
      if (Object.keys(vals).length) base[key] = vals;
      else delete base[key];
      void outboxRef.current.enqueue(opPutSetting("NR_BY", base));
      return { ...prev, NR_BY: base };
    });
  }, []);

  // ShowingTime feed url (Phase 27) — string-valued settings key, whole-value
  // replace like every settings put. Optimistic + outbox, same as putSetting.
  const saveFeedUrl = useCallback((url: string) => {
    setOrgSettings((prev) => ({ ...prev, SHOWINGTIME_FEED: url }));
    void outboxRef.current.enqueue(opPutFeedUrl(url));
  }, []);
  // Parity #3: after an admin invite/role/remove, pull the fresh profiles list.
  const refetchMembers = useCallback(async () => {
    const t = auth.token();
    if (!t) return;
    try { setMembers(await fetchMembers(t)); } catch { /* next boot catches up */ }
  }, [auth]);

  /** Every whole-array ledger write goes through here.
   *
   * These are REPLACE operations: the array you pass becomes the entire value
   * of the key. Callers build that array from `orgSettings`, so if settings
   * were never read, they build it from nothing and the put wipes the ledger.
   * Refusing the write is the only safe answer — the UI gate alone is not
   * enough, because a page can be reached before the flag propagates.
   *
   * Kept deliberately blunt: no queuing, no "save it later". A deferred write
   * would still be built from the empty snapshot.
   */
  const ledgerPut = useCallback((key: string, all: unknown, op: RecOp): boolean => {
    if (!settingsReadyRef.current) {
      diag("ledger.put", { key, accepted: false, why: "settings-not-ready" });
      setSyncNote("Couldn't save — your settings haven't loaded yet. Nothing was changed; try again in a moment.");
      return false;
    }
    lastLedgerWriteRef.current = Date.now();
    // 2026-09-02 (Crest Lake expense that "didn't add"): record WHAT this
    // write changes, relative to the copy this tab holds. Delivery re-applies
    // just that delta onto the server's current array (mergeLedgerOps), so a
    // tab that loaded settings days ago can't overwrite what the phone logged
    // since. No delta (nothing changed) → plain replace, as before.
    const prev = orgSettingsRef.current[key];
    let intentSummary: string | null = null;
    if (Array.isArray(all) && Array.isArray(prev)) {
      const intent = diffLedger(prev, all);
      if (!isEmptyIntent(intent)) op = { ...op, intent };
      intentSummary = "+" + intent.added.length + "/-" + intent.removed.length;
    }
    diag("ledger.put", { key, accepted: true, prevLen: Array.isArray(prev) ? prev.length : -1, nextLen: Array.isArray(all) ? all.length : -1, intent: intentSummary, base: !!op.base });
    setOrgSettings((p) => ({ ...p, [key]: all }));
    void outboxRef.current.enqueue(op).catch((e) => diag("outbox.enqueuefail", { key, msg: String((e as Error)?.message ?? e).slice(0, 120) }));
    return true;
  }, []);

  // Deal contractors/payments — writes the WHOLE TXN_CONTRACTORS array (v1 blob
  // semantics; caller preserves other deals' entries). Gated Phase B client-side.
  const saveContractors = useCallback((all: Record<string, unknown>[]) => {
    ledgerPut("TXN_CONTRACTORS", all, opPutContractors(all));
  }, [ledgerPut]);

  // Expense ledgers (Phase 19): whole-array puts through the settings door, same
  // durable path as contractors. EXP2 = property ledger (tuple rows), COMPANY_EXP
  // = company/QBO expenses (objects). Gated Phase B via liveFire `expensesEdit`.
  // Each returns whether the write was ACCEPTED (queued) — a refused save must
  // keep the form open with the reason, not close it as if it worked.
  const savePropExpenses = useCallback((all: unknown[][]): boolean =>
    ledgerPut("EXP2", all, opPutPropExpenses(all)), [ledgerPut]);
  const saveCompanyExpenses = useCallback((all: Record<string, unknown>[]): boolean =>
    ledgerPut("COMPANY_EXP", all, opPutCompanyExpenses(all)), [ledgerPut]);
  const saveTxnExpenses = useCallback((all: Record<string, unknown>[]): boolean =>
    ledgerPut("TXNEXP", all, opPutTxnExpenses(all)), [ledgerPut]);
  const savePartners = useCallback((all: Partner[]) => {
    ledgerPut("PARTNERS", all, opPutPartners(all));
  }, [ledgerPut]);
  // SOW templates (Phase 28) — the whole org SCOPE_TPLS array in v1's shape,
  // written like every other settings ledger (optimistic + outbox + base etag).
  const saveScopeTpls = useCallback((all: unknown[]) => {
    ledgerPut("SCOPE_TPLS", all, opPutScopeTpls(all));
  }, [ledgerPut]);
  // v1 submitPayment / payInstallment mirror a contractor payout into the deal's
  // TXNEXP ledger as a PAID "Rehab" expense (company = vendor, tax = amount).
  // We post to TXNEXP only — the per-deal Expenses ledger rendered beside the
  // contractor card; v1 additionally mirrors to the EXP2 property ledger, which
  // we intentionally skip to avoid double-counting in v2's expense totals. Rides
  // the same Phase-B settings door as the contractor write.
  const postContractorExpense = useCallback(
    (addr: string, p: { desc: string; amount: number; method: string; vendor: string; date: string }) => {
      const today = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" });
      const row = txnExpenseToRaw({
        deal: addr, date: p.date || today, desc: p.desc, company: p.vendor || "—",
        cat: "Rehab", amount: p.amount, tax: p.amount, status: "PAID", cid: newExpenseCid(),
      });
      saveTxnExpenses([row, ...allTxnExpensesRaw(orgSettings)]);
    },
    [orgSettings, saveTxnExpenses],
  );
  const saveInvestors = useCallback((all: Investor[]) => {
    ledgerPut("INVESTORS", all, opPutInvestors(all));
  }, [ledgerPut]);
  const saveBuyers = useCallback((all: Buyer[]) => {
    ledgerPut("BUYERS", all, opPutBuyers(all));
  }, [ledgerPut]);
  // dispo rolodex for the lead drawer's Matched Buyers panel (v1 matchedBuyersPanel)
  const buyers = useMemo(() => allBuyers(orgSettings), [orgSettings]);
  // NR master (financing assumptions) for the lead Est-ROI tile — same source as
  // the ROI page (org NR_MASTER merged over v1 defaults).
  const nrMaster = useMemo(() => mergeMaster(orgSettings["NR_MASTER"]), [orgSettings]);
  // Wholesaler Review queue (v1 approve/rejectWholesaler): reject drops the item
  // from WHOLESALER_QUEUE; approve also creates a Pre-Check lead via the leads
  // door. Both queue writes are whole-array replaces (settings door).
  const dropFromQueue = useCallback((item: QueueItem) => {
    setOrgSettings((prev) => {
      const cur = allQueue(prev);
      const next = cur.filter((q) => q.id !== item.id);
      void outboxRef.current.enqueue(opPutQueue(next));
      return { ...prev, WHOLESALER_QUEUE: next };
    });
  }, []);
  const rejectWholesalerDeal = useCallback((item: QueueItem) => { dropFromQueue(item); }, [dropFromQueue]);
  const approveWholesalerDeal = useCallback((item: QueueItem) => {
    const row = queueItemToLeadRow(item, Date.now());
    void outboxRef.current.enqueue(opAddLead(row));
    const optimistic = narrowManualLead({ nat_key: manualKey(item.a), address: item.a, source: "Wholesaler", data: row, created_at: new Date().toISOString() });
    if (optimistic) setManualLeads((prev) => [optimistic, ...prev.filter((l) => l.id !== optimistic.id)]);
    dropFromQueue(item);
    setSyncNote(`Approved ${item.a} → added to Properties`);
  }, [dropFromQueue]);
  // Deal Inbox auto-scan preference lives on AUTOMATION.emailScan (v1). Merge it
  // into the existing AUTOMATION object (settings writes replace the key whole).
  const toggleEmailScan = useCallback((on: boolean) => {
    const cur = orgSettings["AUTOMATION"];
    const base = cur !== null && typeof cur === "object" && !Array.isArray(cur) ? (cur as Record<string, unknown>) : {};
    putSetting("AUTOMATION", { ...base, emailScan: on });
  }, [orgSettings, putSetting]);
  // DEAL INBOX AUTO-SCAN engine (2026-07-23): the toggle preference existed but
  // nothing drove it. This is the missing loop — every 10 min, IF the visible
  // AUTOMATION.emailScan toggle is on AND the dealInboxScan gate is live AND a
  // deal-Gmail is connected, run one scan (creates leads from new deal emails).
  // Triple-gated on purpose: the visible toggle is Tom's control; the gate is
  // the go-live fence; the Gmail-connected check avoids pointless calls. Skips
  // hidden tabs so N open tabs don't multiply scans.
  const emailScanOn = ((orgSettings["AUTOMATION"] as Record<string, unknown> | undefined)?.["emailScan"]) === true;
  useEffect(() => {
    if (!emailScanOn || !liveOn("dealInboxScan")) return;
    let alive = true;
    let running = false;
    const tick = async () => {
      if (!alive || running) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      const t = auth.token();
      if (!t || !myProfile?.orgId) return;
      running = true;
      try {
        const st = await fetchInboxStatus(t);
        if (!st.gmailConnected || !st.gmailScope) return; // nothing to scan
        const r = await scanGmail(t, myProfile.orgId);
        if (alive && r.createdCount > 0) { setInboxNote(`Auto-scan: ${r.createdCount} new lead${r.createdCount === 1 ? "" : "s"} from email`); void refetchLeads(); }
      } catch { /* backend asleep / transient — next tick retries */ }
      finally { running = false; }
    };
    void tick();
    const id = setInterval(() => void tick(), 600000); // 10 min
    return () => { alive = false; clearInterval(id); };
  }, [emailScanOn, auth, myProfile, refetchLeads]);

  const changeField = useCallback((address: string, key: string, value: string | null) => {
    const k = natKey(address);
    setEdits((prev) => {
      const next = new Map(prev);
      const merged = { ...(prev.get(k) ?? {}) };
      if (value === null) delete merged[key];
      else merged[key] = value;
      next.set(k, merged);
      return next;
    });
    void outboxRef.current.enqueue(opSetField(address, key, value));
  }, []);

  // Enrich (v1 recheckMarket): market/county/flood pulls → lead_fields edits
  // through the door (coalesced into one patch op); the server stamps the
  // properties row (zest/tax/history), so `stamped` refetches the list. Ends
  // with v1's permit sweep when the lead has none yet.
  const enrichLead = useCallback(async (lead: Lead, onStep: (msg: string) => void): Promise<string> => {
    const fields = editsRef.current.get(natKey(lead.address));
    const out = await runEnrichLead(auth.token(), {
      address: lead.address,
      id: lead.id.startsWith("m:") || lead.id.startsWith("deal:") ? null : lead.id,
      orgId: myProfile?.orgId ?? "",
      county: lead.county,
      fields,
    }, { onWake: () => onStep("waking the backend…"), onStep });
    for (const [key, v] of Object.entries(out.edits)) changeField(lead.address, key, v);
    let permitsNote = "";
    if (!(Array.isArray(lead.permits) && lead.permits.length)) {
      onStep("Searching permits (county + AI)…");
      try {
        const pr = await aiPermitSearch(lead.address, lead.county ?? "", lead.id);
        if (pr.ok && pr.count > 0) permitsNote = ` · ${pr.count} permit${pr.count > 1 ? "s" : ""} found`;
      } catch { /* best-effort, v1 parity */ }
    }
    if (out.stamped) {
      onStep("refreshing the property…");
      await refetchLeads();
    }
    const n = out.enrichedCount + Object.keys(out.edits).length;
    const base =
      n > 0 ? `Enriched ✓ — added ${n} field${n > 1 ? "s" : ""} from Zillow/county`
        : out.floodZone ? "Enriched ✓ — flood zone added"
          : out.detail === "no_scrapfly_key" ? "Flood zone added — Zillow/county fetch path not configured"
            : "Enrich ran — no new data found";
    // The Zillow/county scrape rides on the residential-proxy fetch path
    // (Decodo via fetch_backend — NOT Scrapfly, retired 2026-07; old leads
    // still carry scrapfly_NNN stamps, so match both). When THAT path is the
    // problem, say so instead of letting a thin result read as "nothing new".
    const warn = out.detail && /scrapfly|fetch_\d/i.test(out.detail) && out.detail !== "no_scrapfly_key"
      ? ` · ⚠ Zillow blocked — ${out.detail.replace(/^(scrapfly|fetch)_\d+:\s*/i, "").slice(0, 90)}`
      : "";
    return base + permitsNote + warn;
  }, [auth, myProfile, changeField, aiPermitSearch, refetchLeads]);

  // Lead → Deal conversion (v1 makeTransaction/makeWholesale): pure ops —
  // the deals/wholesale sections have been open since Phases 5/9. The Drive
  // folder call (real folders in Tom's Drive) waits for Phase B; v1 also
  // creates them lazily on first upload.
  const convertLead = useCallback((lead: Lead, kind: "flip" | "wholesale") => {
    const k = natKey(lead.address);
    const f = editsRef.current.get(k);
    if (kind === "flip") {
      const card = flipCardFor(lead, f);
      setDeals((prev) => [
        { natKey: k, address: lead.address, stageIdx: 0, updatedAt: Date.now(), fields: card.edits, rehab: typeof card["rehab"] === "number" ? (card["rehab"] as number) : 0, label: "Flip", partner: null },
        ...prev.filter((d) => d.natKey !== k),
      ]);
      void outboxRef.current.enqueue(opCreateDeal(card));
      const ops = buildStageChecklist(lead.address, stageNameOf(0), tasksRef.current);
      if (ops.length) {
        setTasks((prev) => [
          ...prev,
          ...ops.map((op) => ({
            natKey: op.k,
            title: String((op.d as Record<string, unknown>)?.["t"] ?? ""),
            status: "pending",
            who: null, due: "—", pri: "Medium",
            deal: lead.address, stage: stageNameOf(0), auto: true,
          })),
        ]);
        for (const op of ops) void outboxRef.current.enqueue(op);
      }
      setSyncNote(CONVERT_MSG.flipDone + " — " + CONVERT_MSG.folders);
      setPage("deals");
    } else {
      const w = wholesaleCardFor(lead, f);
      setWholesale((prev) => [
        {
          natKey: k, address: lead.address, updatedAt: Date.now(), asking: null,
          stage: w.stage, seller: w.seller, ucp: w.ucp === "—" ? null : w.ucp,
          buyer: null, fee: null, status: w.status, fields: {},
          offers: [], inquiries: [], interested: [], showings: [],
          contacts: [], team: [], chat: [], comms: [], notes: "", flyer: narrowWtFlyer(undefined),
          tasks: [], files: [], link: "",
        },
        ...prev.filter((x) => x.natKey !== k),
      ]);
      void outboxRef.current.enqueue(opCreateWholesale(w));
      setSyncNote(CONVERT_MSG.wholesaleDone + " — " + CONVERT_MSG.folders);
      setPage("wholesale");
    }
    // v1: p.stage='Deal' on the lead (qual untouched)
    setEdits((prev) => new Map(prev).set(k, { ...(prev.get(k) ?? {}), stage: "Deal" }));
    void outboxRef.current.enqueue(opLeadStageDeal(lead.address));
    // live-fire (Phase B): create the deal's Drive folders. Best-effort — a
    // failure (e.g. Drive not connected) is a tip, never blocks the conversion.
    if (liveOn("driveFolders")) {
      void createTransactionFolders(auth.token(), lead.address)
        .then(() => setSyncNote(DRIVE_MSG.foldersOk + " · " + (lead.address.split(",")[0] ?? "").trim()))
        .catch(() => setSyncNote(DRIVE_MSG.foldersSkip));
    }
  }, [auth]);

  /** Tell the assignee(s) a task landed on them (v1 parity: transactions.js
   * fires taskAssigned per name via userIdByName). Names are matched against
   * the profiles roster — free text that isn't a teammate notifies nobody,
   * which is also why the pickers are dropdowns rather than plain inputs.
   * Assigning to YOURSELF is deliberately silent: you just typed it, and the
   * server-side taskDue scheduler already covers the reminder. */
  const notifyAssignees = useCallback((who: string | undefined, title: string, deal?: string) => {
    const names = (who ?? "").split(",").map((n) => n.trim().toLowerCase()).filter(Boolean);
    if (!names.length) return;
    const mine = sessionUser?.id ?? "";
    const ids = [...new Set(names.map((n) => memberIdByName.get(n)).filter((id): id is string => !!id && id !== mine))];
    if (!ids.length) return;
    void emitNotif(auth.token(), {
      ev: "taskAssigned",
      title: "New task assigned",
      body: title + (deal ? " · " + deal : ""),
      link: deal ? "detail:" + deal : "task",
      // one alert per assignment, not per retry of the same save
      dedupe_key: "assign:" + taskKey(title, deal ?? "") + ":" + ids.join(","),
      to_users: ids,
    }).catch(() => {}); // a notification must never break the save
  }, [auth, memberIdByName, sessionUser]);

  const addTask = useCallback((title: string, opts?: { deal?: string; due?: string; who?: string; pri?: string; status?: string }) => {
    const t = title.trim();
    if (!t) return;
    const key = taskKey(t, opts?.deal ?? "");
    const status = opts?.status ?? "pending";
    setTasks((prev) => [
      { natKey: key, title: t, status, who: opts?.who ?? null, due: opts?.due ?? null, pri: opts?.pri ?? null, deal: opts?.deal ?? null, stage: null, auto: false },
      ...prev.filter((x) => x.natKey !== key),
    ]);
    const put: Parameters<typeof opPutTask>[0] = { key, title: t, status };
    if (opts?.deal !== undefined) put.deal = opts.deal;
    if (opts?.due !== undefined) put.due = opts.due;
    if (opts?.who !== undefined) put.who = opts.who;
    void outboxRef.current.enqueue(opPutTask(put));
    if (opts?.pri) void outboxRef.current.enqueue(opPatchTask(key, { pri: opts.pri }));
    notifyAssignees(opts?.who, t, opts?.deal);
  }, [notifyAssignees]);

  const toggleTask = useCallback((key: string, done: boolean) => {
    // v1 vocabulary: completed / pending (v2 briefly wrote 'done' — reads stay tolerant)
    setTasks((prev) => prev.map((t) => (t.natKey === key ? { ...t, status: done ? "completed" : "pending" } : t)));
    void outboxRef.current.enqueue(opPatchTask(key, { st: done ? "completed" : "pending" }));
  }, []);

  const removeTask = useCallback((key: string) => {
    setTasks((prev) => prev.filter((t) => t.natKey !== key));
    void outboxRef.current.enqueue(opDelTask(key));
  }, []);

  // who / due / pri edits (v1 taskModal fields) — optimistic + one patch op
  const patchTask = useCallback((key: string, patch: { who?: string; due?: string; pri?: string }) => {
    // RE-assignment notifies too, and only on a real change: this fires on
    // every due/priority edit as well, so comparing against the stored value
    // is what keeps it from re-alerting the same person on every keystroke.
    const before = tasksRef.current.find((t) => t.natKey === key);
    if (patch.who !== undefined && patch.who.trim() && patch.who !== (before?.who ?? "")) {
      notifyAssignees(patch.who, before?.title ?? "", before?.deal ?? undefined);
    }
    setTasks((prev) => prev.map((t) => (t.natKey === key ? { ...t, ...patch } : t)));
    void outboxRef.current.enqueue(opPatchTask(key, patch));
  }, [notifyAssignees]);

  const [stageBlock, setStageBlock] = useState<{ deal: string; stage: string; titles: string[] } | null>(null);

  /** v1 setTxnStage semantics: FORWARD moves are blocked while the current
   * stage's checklist has open auto tasks; entering a stage generates its
   * WF_STAGE_TASKS (unless the org's Automation → Stage tasks is off). */
  const moveDealStage = useCallback((natKey: string, stage: number) => {
    const deal = dealsRef.current.find((d) => d.natKey === natKey);
    if (!deal) return;
    const curName = stageNameOf(deal.stageIdx);
    if (stage > deal.stageIdx) {
      const inc = incompleteChecklist(deal.address, curName, tasksRef.current);
      if (inc.length) {
        setStageBlock({ deal: deal.address, stage: curName, titles: inc.map((t) => t.title) });
        return; // v1: "Can't advance yet"
      }
    }
    setStageBlock(null);
    setDeals((prev) => prev.map((d) => (d.natKey === natKey ? { ...d, stageIdx: stage } : d)));
    void outboxRef.current.enqueue(opMoveDealStage(natKey, stage));
    // v1 wfGenerate on stage entry (Automation → Stage tasks, default ON)
    const auto = orgSettings["AUTOMATION"];
    const stageTasksOn = !(auto !== null && typeof auto === "object" && (auto as Record<string, unknown>)["stageTasks"] === false);
    if (stageTasksOn) {
      const ops = buildStageChecklist(deal.address, stageNameOf(stage), tasksRef.current);
      if (ops.length) {
        setTasks((prev) => [
          ...prev,
          ...ops.map((op) => ({
            natKey: op.k,
            title: String((op.d as Record<string, unknown>)?.["t"] ?? ""),
            status: "pending",
            who: null, due: "\u2014", pri: "Medium",
            deal: deal.address, stage: stageNameOf(stage), auto: true,
          })),
        ]);
        for (const op of ops) void outboxRef.current.enqueue(op);
      }
    }
  }, [orgSettings]);

  /** Keeps every deal's DERIVED tasks in step with its dates and its stage.
   *
   * Two jobs, both idempotent, both skipped when Automation → Stage tasks is
   * off:
   *
   * 1. DATE REMINDERS — closing at 7/3/1/0 days and the last day of post
   *    occupancy. These are recomputed continuously rather than generated
   *    once, because a closing date MOVES: push it a week and the four rows
   *    re-date themselves instead of stranding a stale set. Clearing the date
   *    removes the still-open rows, which is what stops the server's taskDue
   *    scheduler nagging about a closing that no longer exists.
   *
   * 2. STAGE BACKFILL — wfGenerate only fires on stage ENTRY, so a deal
   *    already sitting in Closed never receives titles added to the template
   *    afterwards. One pass per deal+stage per WF_TEMPLATE_VERSION, recorded
   *    in localStorage: without that marker this would run on every load and
   *    resurrect any checklist item the user deliberately deleted.
   *
   * Gated on tasksLoaded — see the state declaration for why re-putting a
   * reminder against a stale list is worse than doing nothing. */
  useEffect(() => {
    if (!tasksLoaded || !dealsLoaded || !deals.length) return;
    // Permission gate. Unlike every other write in the app this one fires on
    // LOAD, with nobody clicking anything — so a read-only account (a partner
    // or investor login) would silently generate tasks across the whole board
    // just by opening the CRM. The buttons elsewhere are gated at the UI
    // boundary; this has no button to gate.
    if (canEditDeals !== true || !pageEditable("tasks") || !pageEditable("deals")) return;
    const auto = orgSettings["AUTOMATION"];
    if (auto !== null && typeof auto === "object" && (auto as Record<string, unknown>)["stageTasks"] === false) return;

    let seen: Set<string>;
    try {
      const raw = JSON.parse(localStorage.getItem("crm2_wf_backfill") ?? "{}") as { v?: string; done?: string[] };
      seen = new Set(raw.v === WF_TEMPLATE_VERSION && Array.isArray(raw.done) ? raw.done : []);
    } catch {
      seen = new Set();
    }

    const r = reconcileDeals(deals, tasks, stageNameOf, shiftDealDate, seen);

    if (r.done.size !== seen.size) {
      try {
        localStorage.setItem("crm2_wf_backfill", JSON.stringify({ v: WF_TEMPLATE_VERSION, done: [...r.done] }));
      } catch { /* private mode: the backfill just repeats, harmlessly */ }
    }
    if (!r.ops.length) return;
    setTasks((prev) => [
      ...prev
        .filter((t) => !r.removed.has(t.natKey))
        .map((t) => (r.redated.has(t.natKey) ? { ...t, due: r.redated.get(t.natKey) as string } : t)),
      ...r.added,
    ]);
    for (const op of r.ops) void outboxRef.current.enqueue(op);
  }, [tasksLoaded, dealsLoaded, deals, tasks, orgSettings, canEditDeals, pageEditable]);

  const editDealField = useCallback((natKey: string, field: string, value: string | null) => {
    setDeals((prev) =>
      prev.map((d) =>
        d.natKey === natKey ? { ...d, fields: { ...d.fields, [field]: value } } : d,
      ),
    );
    void outboxRef.current.enqueue(opEditDealField(natKey, field, value));
  }, []);
  // CRM-side name for a Drive file on a deal (Tom 2026-09-07) — the file in
  // Google Drive keeps its own name; this is what the CRM shows for it.
  const labelDriveFile = useCallback((natKey: string, fileId: string, label: string | null) => {
    const lab = label && label.trim() ? label.trim() : null;
    setDeals((prev) =>
      prev.map((d) => {
        if (d.natKey !== natKey) return d;
        const next = { ...(d.driveLabels ?? {}) };
        if (lab) next[fileId] = lab; else delete next[fileId];
        return { ...d, driveLabels: next };
      }),
    );
    void outboxRef.current.enqueue(opDealDriveLabel(natKey, fileId, lab));
  }, []);
  // Selling-AI blobs (selRepairs / selDisclosure …): array/object values on the
  // deal edits, optimistic + queued through the same deals door.
  const saveDealBlob = useCallback((natKey: string, key: string, value: unknown) => {
    setDeals((prev) =>
      prev.map((d) =>
        d.natKey === natKey ? { ...d, fields: { ...d.fields, [key]: value } } : d,
      ),
    );
    void outboxRef.current.enqueue(opEditDealBlob(natKey, key, value));
  }, []);
  // Internal Assignment (v1 saveAssign): deal label + partner {type,name,color,note}.
  // Optimistically patches the TOP-LEVEL narrowed fields (label/partner*) the board
  // card reads — these come off the deal's `data` blob, not `fields`, so a plain
  // saveDealBlob wouldn't repaint the pill until the next poll. Then queues one
  // top-level card patch (opAssignDeal) through the same deals door v1 reads on its
  // poll — label/partner must land at data.label / data.partner, where narrowDeal
  // (and v1's card) look, NOT under data.edits.
  const saveDealAssign = useCallback(
    (natKey: string, label: string | null, partner: { type: string; name: string; color: string; note: string } | null) => {
      setDeals((prev) =>
        prev.map((d) =>
          d.natKey === natKey
            ? {
                ...d,
                label,
                partner: partner ? partner.name : null,
                partnerType: partner ? partner.type : null,
                partnerColor: partner ? partner.color : null,
                partnerNote: partner ? partner.note : null,
              }
            : d,
        ),
      );
      void outboxRef.current.enqueue(opAssignDeal(natKey, label, partner));
    },
    [],
  );
  // v1 deleteTxnCard — soft-delete the deal (sets deleted_at via the shared
  // tombstone). Optimistically drops it from the board, then queues the del op
  // so v1's kanban loses it on its next poll too. Recoverable (deleted_at can be
  // cleared) — not a hard delete. Gated to canEdit at the DealsPage boundary.
  const deleteDeal = useCallback((natKey: string) => {
    setDeals((prev) => prev.filter((d) => d.natKey !== natKey));
    void outboxRef.current.enqueue(opDeleteDeal(natKey));
  }, []);
  // Selling-AI backend parse (addendum PDF → repairs, invoice → matched repairs).
  // Gated at the section by liveFire `sellingAi`; these just wrap the Render calls.
  const sellingAi = useMemo(
    () => ({
      canFire: liveOn("sellingAi"),
      parseAddendum: (pdfB64: string) => parseAddendum(auth.token(), pdfB64),
      parseInvoice: (fileB64: string, mime: string, reps: { id: string; title: string }[]) => parseInvoice(auth.token(), fileB64, mime, reps),
      remarks: (facts: RemarksFacts, sowItems: number, tone: RemarksTone, len: RemarksLength, notes: string) =>
        generateRemarksAi(auth.token(), facts, sowItems, tone, len, notes),
    }),
    [auth],
  );
  // Notifications feed (v1 analytics.js): server-backed, shared with v1. Reads +
  // non-destructive mark-read only. `notifUnread` drives the nav bell badge — a
  // one-time read on load (not a live poll; polling is the gated engine piece).
  const [notifUnread, setNotifUnread] = useState(0);
  useEffect(() => {
    let alive = true;
    const pull = () => { void fetchNotifications(auth.token()).then((rows) => { if (alive) setNotifUnread(rows.filter((n) => !n.read).length); }).catch(() => {}); };
    pull();
    // v1 live-poll (Task #47) — refetch on a 60s cadence, but ONLY when the
    // notifyPoll gate is flipped on. Off by default so the beta doesn't add
    // background load while v1 is the live business; one fetch-on-load still
    // powers the badge. Flip LIVEFIRE_notifyPoll=1 to prove it live.
    const id = liveOn("notifyPoll") ? setInterval(pull, 60000) : null;
    return () => { alive = false; if (id !== null) clearInterval(id); };
  }, [auth]);
  // THREADS live-poll (2026-07-23, SMS self-proof follow-up): threads used to
  // load ONCE at boot, so an inbound reply never reached an open drawer/Inbox
  // until a reload or a send. Same cadence + gate as the bell poll above —
  // one read-only fetch a minute, only while the tab is visible.
  useEffect(() => {
    if (!liveOn("notifyPoll")) return;
    let alive = true;
    const pull = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      const t = auth.token();
      if (!t) return;
      void fetchThreads(t).then((r) => { if (alive) setThreads(r.threads); }).catch(() => { /* next tick catches up */ });
    };
    const id = setInterval(pull, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [auth]);
  const notifLoaders = useMemo(
    () => ({
      list: async () => {
        const rows = await fetchNotifications(auth.token());
        setNotifUnread(rows.filter((n) => !n.read).length);
        return rows;
      },
      read: async (which: { ids: string[] } | { all: true }) => {
        const r = await markNotifsRead(auth.token(), which);
        setNotifUnread((u) => ("all" in which ? 0 : Math.max(0, u - which.ids.length)));
        return r;
      },
    }),
    [auth],
  );

  const openTasks = tasks.filter((t) => !isTaskDone(t.status)).length;
  const inboxUnread = threads ? threads.filter((t) => t.unreadHint).length : 0;
  // v1 parity: the list is `DATA` = manual (rec_leads) + scraped (properties).
  // `leads` stays scraped-only (its cache + lying-read guard); `allLeads` is
  // the merged view every lead surface consumes.
  const allLeads = useMemo(() => [...manualLeads, ...(leads ?? [])], [manualLeads, leads]);
  // 🔥 nav badge = hot replies in the last 48h (v1's badge window); the Hot
  // page itself shows the full archive
  const hotCount = useMemo(() => {
    const now = Date.now();
    let n = 0;
    for (const l of allLeads) if (isHotReply(edits.get(natKey(l.address)), now)) n++;
    return n;
  }, [allLeads, edits]);

  // inbox → lead: match a thread's lead_key (or its address prefix) to a lead
  // Param is the minimal shape (just the messages' leadKeys) so it accepts BOTH
  // the Inbox and the Dialer thread types — the merged comms page is a Dialer thread.
  const findLeadAddress = useCallback(
    (t: { messages: { leadKey: string | null }[] }): string | null => {
      const keys = t.messages.map((m) => m.leadKey).filter((x): x is string => !!x);
      for (const lk of keys) {
        const norm = natKey(lk);
        const hit = allLeads.find((l) => norm === natKey(l.address) || norm.startsWith(natKey(l.address) + ","));
        if (hit) return hit.address;
      }
      return null;
    },
    [allLeads],
  );

  const jumpToLead = useCallback((address: string) => {
    setJumpAddress(address);
    setPage("properties");
  }, []);
  const openWholesaleDeal = useCallback((natKey: string) => {
    setJumpWholesale(natKey);
    setPage("wholesale");
  }, []);
  dealsRef.current = deals;
  tasksRef.current = tasks;
  editsRef.current = edits;

  // Calendar events: whole-group replace (v1's only calendar write shape),
  // optimistic locally + persisted to the cache the boot loader reads.
  const saveCalGroup = useCallback((group: string, events: CalEvent[]) => {
    setCalGroups((prev) => {
      const next = { ...prev, [group]: events };
      void idbSet("calendar_v1", { at: Date.now(), groups: next });
      return next;
    });
    void outboxRef.current.enqueue(opReplaceCalendarGroup(group, events));
  }, []);

  // STALE-READ BANNER (2026-07-17): v1 froze silently during the outage —
  // everyone stared at hours-old boards with no hint. If v2's reads start
  // failing, say so on screen, with the age of what's being shown. Fed by
  // the http layer via lib/health; ticks every 10s so the age stays honest.
  const [fresh, setFresh] = useState(() => freshness());
  useEffect(() => {
    const update = () => setFresh(freshness());
    const un = subscribeHealth(update);
    const t = setInterval(update, 10_000);
    return () => {
      un();
      clearInterval(t);
    };
  }, []);

  // ⚠ RULES OF HOOKS (2026-07-23): this SignIn gate must stay BELOW every hook
  // in App(). It used to sit above the eight hooks now directly above it — so
  // the signed-out render skipped them, the first signed-in render added them,
  // and React crashed every login with minified error #310 ("rendered more
  // hooks than during the previous render"); the ErrorBoundary then wiped the
  // saved view. Add new hooks ABOVE this line, never below.
  if (authState.kind === "no-session" || authState.kind === "signed-out") {
    return <SignIn auth={auth} note={authState.kind === "signed-out" ? authState.reason : undefined} />;
  }

  const pill =
    authState.kind === "repairing"
      ? { cls: "pill warn", text: "Can't reach the sign-in server — showing your saved data. Retrying…" }
      : box.pending > 0
        ? { cls: "pill", text: `${box.pending} change${box.pending === 1 ? "" : "s"} will sync${box.sending ? " — syncing…" : ""}` }
        : box.dead > 0
          ? {
              cls: "pill warn",
              text:
                `${box.dead} change${box.dead === 1 ? " was" : "s were"} rejected by the server` +
                (box.deadReason ? ` — ${box.deadReason}` : "") + " · tap to review",
              dead: true,
            }
          : syncNote
            ? { cls: "pill", text: syncNote }
            : null;

  // ---- Sidebar + top-header model (v1 parity) --------------------------------
  const numField = (v: unknown): number => {
    const n = Number(String(v ?? "").replace(/[^0-9.]/g, ""));
    return Number.isFinite(n) ? n : 0;
  };
  const pipelineNum = deals.reduce((sum, d) => sum + numField(d.fields["t_purchase"]), 0);
  const fmtStat = (n: number): string =>
    n >= 1e6 ? "$" + (n / 1e6).toFixed(1) + "M" : n >= 1e3 ? "$" + Math.round(n / 1e3) + "k" : "$" + Math.round(n);
  const avatarInitial = (myProfile?.name ?? sessionUser?.email ?? "You").trim().charAt(0).toUpperCase() || "U";
  const displayName =
    myProfile?.name?.trim() || (sessionUser?.email ? sessionUser.email.split("@")[0] ?? sessionUser.email : "You");
  const roleLabel = role ? role.charAt(0).toUpperCase() + role.slice(1) : "Member";
  const askAiDeals: AskAiDeal[] = deals.map((d) => ({
    address: d.address,
    arv: numField(d.fields["t_arv"]),
    purchase: numField(d.fields["t_purchase"]),
    rehab: d.rehab || 0,
  }));
  const propBadge =
    leads && leads.length ? (leads.length >= 1000 ? (leads.length / 1000).toFixed(1) + "k" : String(leads.length)) : null;
  type NavDef = { pg: Page; label: string; icon: IconName; badge?: string | null; hot?: boolean };
  const NAV_WORKSPACE: NavDef[] = [
    { pg: "dashboard", label: "Dashboard", icon: "dashboard" },
    { pg: "properties", label: "Properties", icon: "building", badge: propBadge },
    { pg: "deals", label: "Deals", icon: "deals", badge: deals.length ? String(deals.length) : null },
    { pg: "wholesale", label: "Wholesale", icon: "wholesale", badge: wholesale.length ? String(wholesale.length) : null },
    { pg: "tasks", label: "Tasks", icon: "tasks", badge: openTasks ? String(openTasks) : null },
    { pg: "inbox", label: "Inbox", icon: "inbox", badge: inboxUnread ? String(inboxUnread) : null },
    { pg: "hot", label: "Hot", icon: "hot", badge: hotCount ? String(hotCount) : null, hot: true },
    { pg: "mailbox", label: "Emails", icon: "mail" },
    { pg: "messages", label: "Messages", icon: "message" },
    { pg: "contacts", label: "Contacts", icon: "users", badge: contacts.length ? String(contacts.length) : null },
    { pg: "calendar", label: "Calendar", icon: "calendar" },
    { pg: "buyers", label: "Cash Buyers", icon: "cash" },
    // Investors was reachable ONLY from a lead drawer's "match investors" jump —
    // it was in the mobile list but never the sidebar, so on desktop there was
    // no way to open it directly. Same rolodex idea as Cash Buyers; it belongs
    // next to it.
    { pg: "investors", label: "Investors", icon: "usercheck" },
    { pg: "dealinbox", label: "Deal Inbox", icon: "clipboard" },
    { pg: "facebook", label: "Facebook", icon: "message" },
  ];
  const NAV_FINANCES: NavDef[] = [
    { pg: "expenses", label: "Property Expenses", icon: "receipt" },
    { pg: "companyexp", label: "Company Expenses", icon: "briefcase" },
    { pg: "utilities", label: "Utilities", icon: "zap" },
    { pg: "roi", label: "Net ROI Calculator", icon: "calculator" },
  ];
  const NAV_ADMIN: NavDef[] = [
    { pg: "analytics", label: "Analytics", icon: "chart" },
    { pg: "intel", label: "Market Intel", icon: "target" },
    { pg: "pricedrops", label: "Price Reductions", icon: "trendingdown" },
    { pg: "agent", label: "Text Agent", icon: "bot" },
    { pg: "materials", label: "Materials", icon: "package" },
    { pg: "team", label: "Team", icon: "users" },
    { pg: "scrapers", label: "Scrapers", icon: "globe" },
    { pg: "bizfiles", label: "Business Files", icon: "folder" },
    // Reachable from the topbar bell, but the bell is easy to miss on a phone
    // and the old mobile list carried it. Listed so it stays findable.
    { pg: "notifications", label: "Notifications", icon: "bell" },
    { pg: "settings", label: "Settings", icon: "gear" },
  ];
  const crumbLabel =
    [...NAV_WORKSPACE, ...NAV_FINANCES, ...NAV_ADMIN].find((n) => n.pg === page)?.label ?? PAGE_LABELS[page] ?? "";
  const renderNav = (items: NavDef[]) =>
    items.map((it) =>
      canSee(it.pg) ? (
        // real anchor (href=#page) so a middle-click / ⌘-click opens the page in
        // a NEW TAB natively; left-click is intercepted for in-app navigation.
        <a
          key={it.pg}
          href={"#" + it.pg}
          className={"navitem" + (page === it.pg ? " active" : "")}
          onClick={(e) => { e.preventDefault(); goPage(it.pg); }}
        >
          <Icon name={it.icon} size={18} className="navico" />
          <span className="navlabel">{it.label}</span>
          {it.badge ? <span className={"navcount" + (it.hot ? " hotcount" : "")}>{it.badge}</span> : null}
        </a>
      ) : null,
    );

  return (
    <div className="shell">
      <nav className="side">
        <div className="brand">
          <span className="brandlogo">R</span>
          <span className="brandname">REI CRM</span>
          <span className="v2">v2</span>
        </div>
        <button className="sidesearch" title="Search anything (⌘K)" onClick={() => setPaletteOpen(true)}>
          <Icon name="search" size={15} />
          <span className="sidesearchtxt">Search…</span>
          <kbd>⌘K</kbd>
        </button>
        {/* v1 parity: brand · search · grouped icon nav · stats · profile card */}
        <div className="sidenav">
          <div className="navgrouphd">Workspace</div>
          {renderNav(NAV_WORKSPACE)}
          <div className="navgrouphd">Finances</div>
          {renderNav(NAV_FINANCES)}
          <div className="navgrouphd">Admin</div>
          {renderNav(NAV_ADMIN)}
          {isRestrictedRole(role) ? <div className="navrole" title="Your view is limited to your role">Role: {role}</div> : null}
        </div>
        <div className="sidestats">
          <div className="sidestat"><b>{deals.length}</b><span>DEALS</span></div>
          <div className="sidestat"><b>{openTasks}</b><span>TASKS</span></div>
          <div className="sidestat"><b className="pipe">{pipelineNum ? fmtStat(pipelineNum) : "$0"}</b><span>PIPELINE</span></div>
        </div>
        <div className="sideprofile">
          <span className="sideavatar">{avatarInitial}</span>
          <span className="sideprofilemeta">
            <strong>{displayName}</strong>
            <em>{roleLabel}</em>
          </span>
          <button className="iconbtn danger" title="Sign out" onClick={() => auth.signOut()}><Icon name="logout" size={16} /></button>
        </div>
      </nav>
      <main>
        <header className="topbar">
          <div className="crumb">{crumbLabel}</div>
          <div className="topacts">
            <button className="askaibtn" title="Ask AI" onClick={() => setAskAiOpen(true)}>
              <Icon name="sparkles" size={16} />
              <span className="askailbl">Ask AI</span>
            </button>
            <button className="iconbtn topbell" title="Notifications" onClick={() => goPage("notifications")}>
              <Icon name="bell" size={19} />
              {notifUnread ? <span className="bellcount">{notifUnread > 99 ? "99+" : notifUnread}</span> : null}
            </button>
            <button className="iconbtn topdev" title="Mobile preview" onClick={() => setDevOpen(true)}>
              <Icon name="smartphone" size={19} />
            </button>
            <button className="iconbtn" title="Toggle dark mode" onClick={toggleTheme}>
              <Icon name={theme === "dark" ? "moon" : "sun"} size={19} />
            </button>
            <div className="topavatarwrap">
              <button className="topavatar" title="Account" onClick={() => setAcctOpen((v) => !v)}>{avatarInitial}</button>
              {acctOpen ? (
                <>
                  <div className="acctveil" onClick={() => setAcctOpen(false)} />
                  <div className="acctmenu">
                    <div className="acctwho">
                      <strong>{displayName}</strong>
                      <em>{myProfile?.email ?? ""}</em>
                      <em className="acctrole">{roleLabel}</em>
                    </div>
                    <button className="acctitem" onClick={() => { setAcctOpen(false); goPage("settings"); }}>⚙ Settings</button>
                    <button className="acctitem" onClick={() => { setAcctOpen(false); toggleTheme(); }}>{theme === "dark" ? "☀️ Light mode" : "🌙 Dark mode"}</button>
                    <button className="acctitem danger" onClick={() => auth.signOut()}>⎋ Log out</button>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </header>
        {fresh.stale ? (
          <div className="pill stale">
            Can't reach the server — showing saved data
            {fresh.sinceMs !== null ? ` from ${fmtStaleAge(fresh.sinceMs)}` : ""}. Reconnecting…
          </div>
        ) : null}
        {/* .pillfloat wrapper (2026-08-10, Tom): the sync pill used to render
            IN-FLOW, so "1 change will sync" pushed the whole page down and
            snapped it back up when it cleared — on every single edit. The
            wrapper is a zero-height fixed overlay: the pill floats over the
            content and nothing moves, ever. */}
        {pill ? (
          <div className="pillfloat">
          {"dead" in pill && pill.dead ? (
            <button className={pill.cls + " pillclick"} onClick={() => setDeadOpen(outboxRef.current.deadOps())}>{pill.text}</button>
          ) : (
            <div className={pill.cls}>{pill.text}</div>
          )}
          </div>
        ) : null}
        {page === "hot" ? (
          <HotPage
            leads={allLeads}
            edits={edits}
            threads={threads ?? []}
            findLeadAddress={findLeadAddress}
            onOpenLead={jumpToLead}
            onOpenThread={(peer) => {
              setJumpPeer(peer);
              setPage("inbox");
            }}
            onQuickReply={sendLeadSms}
            canText={liveOn("quoSend")}
          />
        ) : page === "dashboard" ? (
          <DashboardPage
            leads={allLeads}
            deals={deals}
            tasks={tasks}
            wholesale={wholesale}
            inboxUnread={inboxUnread}
            onNav={setPage}
            onToggleTask={toggleTask}
            onOpenLead={jumpToLead}
          />
        ) : page === "inbox" ? (
          <DialerPage
            token={auth.token() ?? ""}
            orgId={myProfile?.orgId ?? ""}
            canSend={liveOn("quoSend")}
            canCall={liveOn("quoCall")}
            onWake={() => setSyncNote("waking the backend…")}
            findLeadAddress={findLeadAddress}
            contacts={contacts}
            onOpenLead={jumpToLead}
            openPeer={jumpPeer}
            onOpenedPeer={() => setJumpPeer(null)}
          />
        ) : page === "deals" ? (
          <DealsPage
            deals={deals}
            contacts={contacts}
            pastContacts={pastContacts}
            tasks={tasks}
            ready={dealsReady}
            note={dealsNote}
            canEdit={canEditDeals === true && pageEditable("deals")}
            canEditProbing={canEditDeals === null}
            orgSettings={orgSettings}
            edits={edits}
            members={taskMemberNames}
            onToggleTask={toggleTask}
            onAddTask={addTask}
            onMoveStage={moveDealStage}
            onEditField={editDealField}
            onLabelDriveFile={labelDriveFile}
            stageBlock={stageBlock}
            onDismissBlock={() => setStageBlock(null)}
            onOpenTasks={() => setPage("tasks")}
            driveLoaders={driveLoaders}
            mailLoaders={mailLoaders}
            fetchSow={sowFetch}
            onSowOps={sowOps}
            onSowForceSave={sowForceSave}
            materials={materials.rows}
            onNeedMaterials={refreshMaterials}
            onPutMaterial={putMaterial}
            materialCats={materials.cats}
            onImportUrl={(url, onWake) => lookupMaterialPrice(auth.token(), url, { onWake })}
            onSaveContractors={saveContractors}
            onPostContractorExpense={postContractorExpense}
            onSaveTxnExpenses={saveTxnExpenses}
            onSaveScopeTpls={saveScopeTpls}
            onSaveDealBlob={saveDealBlob}
            onSaveAssign={saveDealAssign}
            onDeleteDeal={gateEdit(canAct("delete"), deleteDeal)}
            token={auth.token()}
            isAdmin={myProfile?.role === "admin"}
            onOpenProperty={jumpToLead}
            jumpKey={jumpDeal}
            onJumped={() => setJumpDeal(null)}
            onOpenChange={setOpenDealKey}
            me={(myProfile?.name ?? sessionUser?.email ?? "Tom").trim().charAt(0).toUpperCase() || "T"}
            sellingAi={sellingAi}
            leads={allLeads}
            onLeadField={changeField}
            onCompsPersist={persistCompsTool}
            onCompsArv={applyCompsArv}
            compsSearch={compsSearch}
            compsPhotos={compsPhotos}
            onRunArv={runArv}
            onSaveArvResult={saveArvResult}
            onAiPermitSearch={aiPermitSearch}
            initialTab={openDealTab}
            onTabChange={setOpenDealTab}
            initialSub={openDealSub}
            onSubChange={setOpenDealSub}
            onShowingFeed={showingFeed}
            uiPrefs={uiPrefs}
            onSaveUiPrefs={saveUiPrefs}
          />
        ) : page === "wholesale" ? (
          <WholesalePage
            rows={wholesale} note={wholesaleNote} canEdit={pageEditable("wholesale")}
            onEdit={editWholesale} onEditField={editWholesaleField} onDispo={saveDispo}
            onWtList={saveWtList} onWtNotes={saveWtNotes} onSaveFlyer={saveWtFlyer}
            onOpenProperty={jumpToLead} buyers={buyers}
            jumpKey={jumpWholesale} onJumped={() => setJumpWholesale(null)}
            onOpenChange={setOpenWholesaleKey}
            me={(myProfile?.name ?? sessionUser?.email ?? "Tom").trim().charAt(0).toUpperCase() || "T"}
          />
        ) : page === "roi" ? (
          <RoiPage leads={allLeads} edits={edits} orgSettings={orgSettings} onSaveRehab={saveRehabCalc} onSaveNrOverride={saveNrOverride} />
        ) : page === "calendar" ? (
          <CalendarPage groups={calGroups} tasks={tasks} deals={deals} onSaveGroup={gateEdit(pageEditable("calendar"), saveCalGroup)} me={myProfile?.id ? { id: myProfile.id, name: myProfile.name ?? "" } : null} token={auth.token()} />
        ) : page === "team" ? (
          <TeamPage members={members} note={null} />
        ) : page === "settings" ? (
          <SettingsPage myProfile={myProfile} members={members} orgSettings={orgSettings} onPutSetting={putSetting} token={auth.token() ?? ""} deals={deals.map((d) => ({ key: d.natKey, label: d.address }))} onSavePartners={savePartners} onSaveFeedUrl={saveFeedUrl} onMembersChanged={refetchMembers} onSaveScopeTpls={saveScopeTpls} materials={materials.rows} onNeedMaterials={refreshMaterials} />
        ) : page === "analytics" ? (
          <AnalyticsPage
            leads={allLeads}
            edits={edits}
            deals={deals}
            tasks={tasks}
            wholesale={wholesale}
            members={members}
            orgSettings={orgSettings}
            threads={threads}
            onOpenDeals={() => setPage("deals")}
          />
        ) : page === "intel" ? (
          <IntelPage leads={allLeads} edits={edits} outcomeLog={orgSettings["OUTCOME_LOG"]} onOpenLead={jumpToLead} />
        ) : page === "facebook" ? (
          <FacebookPage leads={allLeads} onOpenLead={jumpToLead}
            automation={orgSettings["AUTOMATION"]}
            onSaveAutomation={liveOn("settingsEdit") ? (v) => putSetting("AUTOMATION", v) : undefined}
            onAddProperty={liveOn("addProperty") ? addProperty : undefined} />
        ) : page === "agent" ? (
          <AgentPage
            agentRaw={orgSettings["AGENT"]} onOpenLead={jumpToLead} fetchFresh={agentFetchFresh} onPutAgent={putAgent}
            ptextOn={(() => { const p = orgSettings["PTEXT"]; return p !== null && typeof p === "object" && !Array.isArray(p) && (p as Record<string, unknown>)["on"] === true; })()}
            onTogglePtext={(on) => {
              const prev = orgSettings["PTEXT"];
              const base = prev !== null && typeof prev === "object" && !Array.isArray(prev) ? (prev as Record<string, unknown>) : {};
              // enabledAt anchors the Mark-Saba "only future things" fence to the
              // FIRST activation and never moves after that
              const next = { ...base, on, enabledAt: typeof base["enabledAt"] === "number" && base["enabledAt"] ? base["enabledAt"] : (on ? Date.now() : 0) };
              ledgerPut("PTEXT", next, { s: "settings", op: "put", k: "PTEXT", d: next });
            }}
          />
        ) : page === "bizfiles" ? (
          <BizFilesPage loaders={bizLoaders} />
        ) : page === "materials" ? (
          <MaterialsPage
            rows={materials.rows} cats={materials.cats} ready={materials.ready}
            {...(materials.error ? { loadError: materials.error } : {})} onRefresh={refreshMaterials}
            canEdit={materialsCanEdit && pageEditable("materials")}
            onPutMaterial={putMaterial} onDelMaterial={deleteMaterial}
            onPutCat={putMatCat} onDelCat={deleteMatCat}
            onImportUrl={(url, onWake) => lookupMaterialPrice(auth.token(), url, { onWake })}
          />
        ) : page === "expenses" ? (
          <ExpensesPage orgSettings={orgSettings} onSave={savePropExpenses} onSaveTxn={saveTxnExpenses} prefill={expPrefill} onPrefillConsumed={() => setExpPrefill(null)} dealAddresses={dealAddressUnique} settingsReady={settingsReady} />
        ) : page === "companyexp" ? (
          <CompanyExpensesPage orgSettings={orgSettings} onSave={saveCompanyExpenses} token={auth.token() ?? ""} settingsReady={settingsReady} />
        ) : page === "utilities" ? (
          <UtilitiesPage deals={deals} tasks={tasks} onEditField={editDealField} onAddTask={addTask} onOpenDeal={(k) => { setJumpDeal(k); setPage("deals"); }} onLogExpense={(addr) => { setExpPrefill({ desc: addr + " — utilities", category: "Utilities", property: addr }); setPage("expenses"); }} />
        ) : page === "investors" ? (
          <InvestorsPage leads={leads ?? []} orgSettings={orgSettings} onSave={saveInvestors} canEdit={liveOn("settingsEdit")} onBack={() => setPage("properties")} />
        ) : page === "buyers" ? (
          <BuyersPage orgSettings={orgSettings} onSave={saveBuyers} canEdit={liveOn("settingsEdit")} />
        ) : page === "pricedrops" ? (
          <PriceReductionsPage leads={allLeads} onOpenLead={jumpToLead} />
        ) : page === "notifications" ? (
          <NotificationsPage load={notifLoaders.list} onMarkRead={notifLoaders.read} onOpenLead={jumpToLead} onOpenPage={(p) => setPage(p as typeof page)} onOpenConvo={(ph) => { setJumpPeer(ph); setPage("inbox"); }} token={auth.token() ?? ""} />
        ) : page === "dealinbox" ? (
          <DealInboxPage
            loadStatus={dealInboxLoaders.status}
            onParseText={dealInboxLoaders.parseText}
            onParseImage={dealInboxLoaders.parseImage}
            onScan={dealInboxLoaders.scan}
            canFire={liveOn("dealInbox")}
            autoScan={((orgSettings["AUTOMATION"] as Record<string, unknown> | undefined)?.["emailScan"]) === true}
            onToggleAutoScan={toggleEmailScan}
          />
        ) : page === "scrapers" ? (
          <ScrapersPage
            loadHealth={scraperLoaders.health}
            loadStats={scraperLoaders.stats}
            loadJobs={scraperLoaders.jobs}
            onSave={scraperLoaders.save}
            onToggle={scraperLoaders.toggle}
            onDelete={scraperLoaders.del}
            onPauseAll={scraperLoaders.pauseAll}
            canEdit={liveOn("scrapersEdit")}
            queue={allQueue(orgSettings)}
            onApproveQueue={approveWholesalerDeal}
            onRejectQueue={rejectWholesalerDeal}
            token={auth.token()}
            isAdmin={myProfile?.role === "admin"}
          />
        ) : page === "mailbox" ? (
          <MailboxPage loaders={mailboxLoaders} isAdmin={myProfile?.role === "admin"} />
        ) : page === "messages" ? (
          <MessagesPage wholesale={wholesale} onOpenDeal={openWholesaleDeal} />
        ) : page === "dialer" ? (
          // merged into Inbox; kept as a wired fallback for any stale link
          <DialerPage
            token={auth.token() ?? ""}
            orgId={myProfile?.orgId ?? ""}
            canSend={liveOn("quoSend")}
            canCall={liveOn("quoCall")}
            onWake={() => setSyncNote("waking the backend…")}
            findLeadAddress={findLeadAddress}
            contacts={contacts}
            onOpenLead={jumpToLead}
            openPeer={jumpPeer}
            onOpenedPeer={() => setJumpPeer(null)}
          />
        ) : page === "contacts" ? (
          <ContactsPage
            rows={contacts}
            note={contactsNote}
            threads={threads ?? []}
            dealAddresses={dealAddressList}
            edits={edits}
            onOpenThread={(peer) => {
              setJumpPeer(peer);
              setPage("inbox");
            }}
            onSave={pageEditable("contacts") ? saveContact : null}
            onDelete={pageEditable("contacts") ? deleteContact : null}
            onMerge={pageEditable("contacts") ? mergeContacts : null}
          />
        ) : page === "tasks" ? (
          <TasksPage
            tasks={tasks}
            members={taskMemberNames}
            dealKeys={dealKeySet}
            wholesaleKeys={wholesaleKeySet}
            onAdd={gateEdit(pageEditable("tasks"), addTask)}
            onToggle={gateEdit(pageEditable("tasks"), toggleTask)}
            onRemove={gateEdit(pageEditable("tasks"), removeTask)}
            onPatch={gateEdit(pageEditable("tasks"), patchTask)}
          />
        ) : leads === null ? (
          <div className="empty">Loading your leads…</div>
        ) : (
          <LeadsPage
            leads={allLeads}
            syncNote={syncNote}
            edits={edits}
            tasks={tasks}
            onStatus={gateEdit(pageEditable("properties"), changeStatus)}
            onNotes={gateEdit(pageEditable("properties"), changeNotes)}
            onArv={gateEdit(pageEditable("properties"), changeArv)}
            onField={gateEdit(pageEditable("properties"), changeField)}
            onBulkStatus={gateEdit(pageEditable("properties"), bulkStatus)}
            onBulkDelete={gateEdit(pageEditable("properties") && canAct("delete"), bulkDelete)}
            sharedViews={sharedViews}
            onSaveSharedView={saveSharedView}
            onDeleteSharedView={deleteSharedView}
            onAddTask={addTask}
            onToggleTask={toggleTask}
            onPatchTask={patchTask}
            onRemoveTask={removeTask}
            openAddress={jumpAddress}
            onOpenedAddress={() => setJumpAddress(null)}
            onOpenChange={setOpenLeadAddr}
            initialLeadTab={openLeadTab}
            onLeadTabChange={setOpenLeadTab}
            onEnrich={enrichLead}
            threads={threads ?? []}
            onSendSms={sendLeadSms}
            onCallLead={callLead}
            pastContacts={pastContacts}
            outcomeLog={orgSettings["OUTCOME_LOG"]}
            onCompsPersist={persistCompsTool}
            onCompsArv={applyCompsArv}
            compsSearch={compsSearch}
            compsPhotos={compsPhotos}
            onRunArv={runArv}
            onSaveArvResult={saveArvResult}
            mailLoaders={mailLoaders}
            onAiPermitSearch={aiPermitSearch}
            dealKeys={dealKeySet}
            wholesaleKeys={wholesaleKeySet}
            onConvert={gateEdit(canAct("convert"), convertLead)}
            token={auth.token() ?? ""}
            onAddProperty={addProperty}
            onWake={() => setSyncNote("waking the backend…")}
            buyers={buyers}
            onSaveRehab={saveRehabCalc}
            onSaveSeller={saveSeller}
            onRenameLead={renameLead}
            onSaveMediaLinks={saveMediaLinks}
            nrMaster={nrMaster}
            nrBy={orgSettings["NR_BY"]}
            onSaveNrOverride={saveNrOverride}
            uiPrefs={uiPrefs}
            onSaveUiPrefs={saveUiPrefs}
            hiddenFields={hiddenFields}
            onGoInvestors={() => setPage("investors")}
            members={activeMemberList}
            leadScope={leadScopeFor(orgSettings["PERMS"], myProfile?.id, role)}
            myId={myProfile?.id ?? null}
            isAdmin={role === "admin"}
          />
        )}
      </main>
      <BottomNav
        page={page}
        onNav={(p) => goPage(p as Page)}
        // Same arrays the sidebar renders, so the two navs cannot drift apart
        // again — the old hand-maintained copy already had (retired Dialer,
        // plus Investors and Notifications the sidebar never listed).
        groups={[
          { title: "Workspace", items: NAV_WORKSPACE },
          { title: "Finances", items: NAV_FINANCES },
          { title: "Admin", items: NAV_ADMIN },
        ].map((g) => ({
          title: g.title,
          items: g.items
            .filter((n) => canSee(n.pg))
            .map((n) => ({ key: n.pg, label: n.label, icon: n.icon, badge: n.badge ?? null, hot: n.hot ?? false })),
        }))}
        extras={[
          { label: "⌘K Search", onClick: () => setPaletteOpen(true) },
          { label: theme === "dark" ? "☀️ Light mode" : "🌙 Dark mode", onClick: toggleTheme },
          { label: "Sign out", onClick: () => auth.signOut() },
        ]}
      />
      <Palette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        leads={allLeads}
        deals={deals}
        contacts={contacts}
        tasks={tasks}
        wholesale={wholesale}
        nav={(p) => setPage(p as Page)}
        openLead={jumpToLead}
      />
      {askAiOpen ? (
        <AskAiModal deals={askAiDeals} propertiesCount={allLeads.length} openTasks={openTasks} onClose={() => setAskAiOpen(false)} />
      ) : null}
      {devOpen ? <DevicePreview onClose={() => setDevOpen(false)} /> : null}
      {deadOpen ? (
        <DeadOpsModal
          ops={deadOpen}
          onRetry={() => { setDeadOpen(null); void outboxRef.current.retryDead(); }}
          onDiscard={() => { setDeadOpen(null); void outboxRef.current.clearDead(); }}
          onClose={() => setDeadOpen(null)}
        />
      ) : null}
    </div>
  );
}
